/**
 * AGROMX Frontend - Tracking Activos View
 * Vista principal de tracking GPS con lista de vehículos y mapa
 */

import trackingService from '../services/tracking.service.js';
import permisos from '../services/permissions.service.js';
import authService from '../services/auth.service.js';
import unidadesSvc from '../services/unidades.service.js';
import { permisosPantalla, Pestanas } from '../utils/pestanasErp.js';

const escT = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
/**
 * Carga dinámica de Google Maps API (canal estable: v=beta + libreria marker a veces oculta Marker legado)
 */
function loadGoogleMaps() {
  if (window.google?.maps) return Promise.resolve();
  return new Promise((resolve, reject) => {
    window.initMap = resolve;
    const script = document.createElement('script');
    script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyDXUl5_zF6gnVhKYwrTbMHkwgFgb-5x8pM&libraries=geometry&callback=initMap';
    script.async = true;
    script.onerror = () => reject(new Error('Error cargando Google Maps'));
    document.head.appendChild(script);
  });
}

/** Lat/lng validos para el mapa (evita !lat que falla con 0 o string vacia) */
function coordsValidas(lat, lng) {
  const la = typeof lat === 'number' ? lat : parseFloat(String(lat).replace(',', '.'));
  const lo = typeof lng === 'number' ? lng : parseFloat(String(lng).replace(',', '.'));
  return Number.isFinite(la) && Number.isFinite(lo) && Math.abs(la) <= 90 && Math.abs(lo) <= 180;
}

/**
 * Mostrar pin: coords validas + ultimo reporte real (lista y panel usan formatFechaHora === 'Sin datos').
 * Cubre fechas invalidas desde MySQL que son truthy pero no son un reporte util.
 */
function vehiculoMostrarPinEnMapa(v) {
  if (!v) return false;
  if (!coordsValidas(v.latitude, v.longitude)) return false;
  const fh = trackingService.formatFechaHora(v.fecha_hora);
  if (fh === 'Sin datos') return false;
  return true;
}

export default {
  data: {
    vehiculos: [],
    loading: true,
    stats: { total: 0, encendidos: 0, apagados: 0, en_movimiento: 0 },
    filters: { search: '', estado: '' },
    activeStatFilter: null,
    activeTab: 'dispositivos',
    pagination: { page: 1, limit: 50, total: 0, totalPages: 0 },
    map: null,
    markers: {},
    infoWindow: null,
    selectedVehicleId: null,
    refreshInterval: null,
    detailPanelOpen: false,
    detailPanelType: null,
    detailVehicle: null,
    // Centro de notificaciones: historial desde BD; badge solo muestra las no vistas
    notificaciones: [],
    notifLimit: 10,
    notifPanelOpen: false,
    notifPollInterval: null,
    notifLastSeenId: 0,
    _notifDocClickBound: null,
    _notifSwBound: null
  },

  async render() {
    // El usuario distribuidor solo ve sus vehículos: sin filtros ni listado por distribuidor
    const esDistribuidor = authService.hasRole('distribuidor');
    // ERP F1 · B3: patrón de 2 pestañas. «Consultar» = el mapa y el listado de siempre. «Agregar / editar» = ligar cada GPS
    // a su unidad (D10); sólo existe con tracking.editar. El candado real está en el servidor (PUT /api/unidades/gps/vehiculos/:id).
    const ppTrk = permisosPantalla('tracking', { crear: null, editar: 'editar' });
    this.data.conLiga = ppTrk.puedeEditar;
    const filtroDistribuidorTab = (prefix) => esDistribuidor ? '' : `
                  <div class="form-group" style="margin:0; flex:1; min-width:100px;">
                    <input type="text" class="form-control" id="${prefix}FilterDistribuidor" placeholder="Distribuidor" style="font-size:0.8rem;">
                  </div>`;
    return `
      <div class="page-header">
        <h1>Tracking GPS</h1>
        <div style="display: flex; gap: 0.5rem; align-items: center;">
          ${esDistribuidor ? '' : '<button class="btn btn-secondary btn-sm" id="btnDistribuidores" title="Distribuidores y vehículos asignados">&#x1F3E2; Distribuidores</button>'}
          <div class="tracking-notif-wrap">
            <button type="button" class="tracking-notif-btn" id="btnNotifCenter" title="Notificaciones" aria-label="Notificaciones">
              &#128276;
              <span class="tracking-notif-badge" id="notifBadge">0</span>
            </button>
            <div class="tracking-notif-panel" id="notifPanel" role="dialog" aria-label="Ultimas notificaciones">
              <div class="tracking-notif-header">
                <span>Ultimas notificaciones</span>
                <!--<span class="tracking-notif-source">Desde base de datos</span>-->
              </div>
              <div class="tracking-notif-list" id="notifList">
                <div class="tracking-notif-empty">Cargando...</div>
              </div>
              <div class="tracking-notif-footer">
                <button type="button" class="btn btn-secondary btn-sm" id="btnNotifVerMas">Ver mas</button>
                <button type="button" class="btn btn-secondary btn-sm" id="btnNotifClose">Cerrar</button>
              </div>
            </div>
          </div>
          <span class="badge badge-info" id="lastUpdate" style="font-size: 0.8rem;">Actualizando...</span>
          <button class="btn btn-secondary btn-sm" id="btnRefresh" title="Actualizar">&#8635; Actualizar</button>
        </div>
      </div>

      <div class="erp-pestanas" role="tablist" id="trk_pestanas">
        <button type="button" class="erp-pestana activa" role="tab" aria-selected="true" data-pestana="consultar" id="trk_tabConsultar">Consultar</button>
        ${ppTrk.puedeEditar ? '<button type="button" class="erp-pestana" role="tab" aria-selected="false" data-pestana="formulario" id="trk_tabFormulario">Agregar / editar<span class="erp-pestana-etiqueta" id="trk_tabEtiqueta"> · liga GPS ↔ unidad</span></button>' : ''}
      </div>
      <div class="erp-panel activo" role="tabpanel" data-panel="consultar" id="trk_panelConsultar">

      <!-- Estadísticas compactas -->
      <div class="tracking-stats-bar" id="statsContainer">
        <button class="tracking-stat-chip" id="statChipTotal" onclick="trackingView.filterByStat(null)">
          <span class="tracking-stat-icon">&#x1F69C;</span>
          <span class="tracking-stat-value" id="statTotal">-</span>
          <span class="tracking-stat-label">Total</span>
        </button>
        <button class="tracking-stat-chip" id="statChipEncendidos" onclick="trackingView.filterByStat('encendido')" title="Motor encendido (azul)">
          <span class="tracking-stat-dot dot-on"></span>
          <span class="tracking-stat-value" id="statEncendidos">-</span>
          <span class="tracking-stat-label">Encendidos</span>
        </button>
        <button class="tracking-stat-chip" id="statChipApagados" onclick="trackingView.filterByStat('apagado')" title="Motor apagado (gris)">
          <span class="tracking-stat-dot dot-off"></span>
          <span class="tracking-stat-value" id="statApagados">-</span>
          <span class="tracking-stat-label">Apagados</span>
        </button>
        <button class="tracking-stat-chip stat-chip-info" id="statChipMovimiento" onclick="trackingView.filterByStat('movimiento')">
          <span class="tracking-stat-icon">&#x1F504;</span>
          <span class="tracking-stat-value" id="statMovimiento">-</span>
          <span class="tracking-stat-label">En Movimiento</span>
        </button>
      </div>
      <div class="tracking-reporte-legend" title="Color del punto y del marker (filtro Total)">
        <span class="tracking-reporte-legend-item"><span class="vehicle-dot-reporte" style="background:#9ca3af"></span> Sin datos</span>
        <span class="tracking-reporte-legend-item"><span class="vehicle-dot-reporte" style="background:#22c55e"></span> &lt; 1 dia</span>
        <span class="tracking-reporte-legend-item"><span class="vehicle-dot-reporte" style="background:#f59e0b"></span> 1-10 dias</span>
        <span class="tracking-reporte-legend-item"><span class="vehicle-dot-reporte" style="background:#ef4444"></span> &gt; 10 dias</span>
        <span class="tracking-reporte-legend-item"><span class="vehicle-dot-reporte" style="background:#2563eb"></span> Motor encendido (anillo)</span>
      </div>

      <!-- Layout principal: lista + mapa -->
      <div class="tracking-layout">
        <!-- Panel izquierdo: tabs + lista -->
        <div class="tracking-sidebar">
          <!-- Tabs de secciones -->
          <div class="tracking-tabs">
            <button class="tracking-tab active" data-tab="dispositivos" onclick="trackingView.switchTab('dispositivos')">Dispositivos</button>
            <button class="tracking-tab" data-tab="recorridos" onclick="trackingView.switchTab('recorridos')">Recorridos</button>
            <button class="tracking-tab" data-tab="alertas" onclick="trackingView.switchTab('alertas')">Alertas</button>
            <button class="tracking-tab" data-tab="eventos" onclick="trackingView.switchTab('eventos')">Eventos</button>
          </div>

          <!-- Contenido de tabs -->
          <div class="tracking-tab-content" id="tabContent">
            <!-- Tab Dispositivos -->
            <div class="tracking-tab-pane active" id="pane-dispositivos">
              <div class="tracking-filters">
                <input type="text" id="searchInput" class="form-control" placeholder="${esDistribuidor ? 'Buscar por modelo o serie...' : 'Buscar por modelo, serie o distribuidor...'}" />
              </div>
              <div class="tracking-table-header">
                <span class="tracking-col-activo">Serie</span>
                <span class="tracking-col-reporte">Reporte</span>
                <span class="tracking-col-acciones">Acciones</span>
              </div>
              <div class="tracking-vehicle-list" id="vehicleList">
                <div style="padding: 2rem; text-align: center; color: var(--text-muted);">Cargando veh&iacute;culos...</div>
              </div>
            </div>

            <!-- Tab Recorridos -->
            <div class="tracking-tab-pane" id="pane-recorridos">
              <div class="tracking-tab-filters">
                <div style="display:flex; gap:0.4rem; flex-wrap:wrap; align-items:flex-end;">
                  <div class="form-group" style="margin:0; flex:1; min-width:100px;">
                    <label class="form-label" style="font-size:0.72rem;">Filtrar activos</label>
                    <input type="text" class="form-control" id="recFilterPlaca" placeholder="Placa" style="font-size:0.8rem;">
                  </div>
                  <div class="form-group" style="margin:0; flex:1; min-width:100px;">
                    <input type="text" class="form-control" id="recFilterNombre" placeholder="N&uacute;m. serie" style="font-size:0.8rem;">
                  </div>${filtroDistribuidorTab('rec')}
                </div>
              </div>
              <div class="tracking-rec-vehicle-list" id="recVehicleList" style="max-height:140px; overflow-y:auto; border-bottom:1px solid var(--gray-200);"></div>
              <div class="tracking-tab-filters" style="border-bottom:1px solid var(--gray-200);">
                <label class="form-label" style="font-size:0.72rem; margin-bottom:0.2rem;">Rango de fechas</label>
                <div style="display:flex; gap:0.4rem; align-items:center;">
                  <input type="date" class="form-control" id="recFechaInicio" style="font-size:0.8rem; flex:1;">
                  <input type="date" class="form-control" id="recFechaFin" style="font-size:0.8rem; flex:1;">
                </div>
                <div style="display:flex; gap:0.4rem; margin-top:0.4rem;">
                  <button class="btn btn-primary btn-sm" style="flex:1;" onclick="trackingView.verRecorridosTab()">Ver Recorridos</button>
                  <button class="btn btn-secondary btn-sm" style="flex:1;" onclick="trackingView.quitarRecorridosTab()">Quitar Recorridos</button>
                </div>
              </div>
              <div id="recTabResult" style="flex:1; overflow-y:auto; padding:0.5rem; font-size:0.82rem; color:var(--text-muted); text-align:center;">
                Seleccione uno o m&aacute;s activos y un rango de fechas
              </div>
            </div>

            <!-- Tab Alertas -->
            <div class="tracking-tab-pane" id="pane-alertas">
              <div class="tracking-tab-filters">
                <div style="display:flex; gap:0.4rem; flex-wrap:wrap; align-items:flex-end;">
                  <div class="form-group" style="margin:0; flex:1; min-width:100px;">
                    <label class="form-label" style="font-size:0.72rem;">Filtrar activos</label>
                    <input type="text" class="form-control" id="altFilterPlaca" placeholder="Placa" style="font-size:0.8rem;">
                  </div>
                  <div class="form-group" style="margin:0; flex:1; min-width:100px;">
                    <input type="text" class="form-control" id="altFilterNombre" placeholder="N&uacute;m. serie" style="font-size:0.8rem;">
                  </div>${filtroDistribuidorTab('alt')}
                </div>
              </div>
              <div class="tracking-rec-vehicle-list" id="altVehicleList" style="max-height:140px; overflow-y:auto; border-bottom:1px solid var(--gray-200);"></div>
              <div class="tracking-tab-filters" style="border-bottom:1px solid var(--gray-200);">
                <label class="form-label" style="font-size:0.72rem; margin-bottom:0.2rem;">Rango de fechas</label>
                <div style="display:flex; gap:0.4rem; align-items:center;">
                  <input type="date" class="form-control" id="altFechaInicio" style="font-size:0.8rem; flex:1;">
                  <input type="date" class="form-control" id="altFechaFin" style="font-size:0.8rem; flex:1;">
                </div>
                <div style="display:flex; gap:0.4rem; margin-top:0.4rem;">
                  <button class="btn btn-primary btn-sm" style="flex:1;" onclick="trackingView.verAlertasTab()">Aplicar filtro</button>
                  <button class="btn btn-secondary btn-sm" style="flex:1;" onclick="trackingView.limpiarTabFiltro('alt')">Limpiar</button>
                </div>
              </div>
              <div id="altTabResult" style="flex:1; overflow-y:auto; padding:0.5rem; font-size:0.82rem; color:var(--text-muted); text-align:center;">
                Seleccione un activo y un rango de fechas
              </div>
            </div>

            <!-- Tab Eventos -->
            <div class="tracking-tab-pane" id="pane-eventos">
              <div class="tracking-tab-filters">
                <div style="display:flex; gap:0.4rem; flex-wrap:wrap; align-items:flex-end;">
                  <div class="form-group" style="margin:0; flex:1; min-width:100px;">
                    <label class="form-label" style="font-size:0.72rem;">Filtrar activos</label>
                    <input type="text" class="form-control" id="evtFilterPlaca" placeholder="Placa" style="font-size:0.8rem;">
                  </div>
                  <div class="form-group" style="margin:0; flex:1; min-width:100px;">
                    <input type="text" class="form-control" id="evtFilterNombre" placeholder="N&uacute;m. serie" style="font-size:0.8rem;">
                  </div>${filtroDistribuidorTab('evt')}
                </div>
              </div>
              <div class="tracking-rec-vehicle-list" id="evtVehicleList" style="max-height:140px; overflow-y:auto; border-bottom:1px solid var(--gray-200);"></div>
              <div class="tracking-tab-filters" style="border-bottom:1px solid var(--gray-200);">
                <label class="form-label" style="font-size:0.72rem; margin-bottom:0.2rem;">Rango de fechas</label>
                <div style="display:flex; gap:0.4rem; align-items:center;">
                  <input type="date" class="form-control" id="evtFechaInicio" style="font-size:0.8rem; flex:1;">
                  <input type="date" class="form-control" id="evtFechaFin" style="font-size:0.8rem; flex:1;">
                </div>
                <div style="display:flex; gap:0.4rem; margin-top:0.4rem;">
                  <button class="btn btn-primary btn-sm" style="flex:1;" onclick="trackingView.verEventosTab()">Aplicar filtro</button>
                  <button class="btn btn-secondary btn-sm" style="flex:1;" onclick="trackingView.limpiarTabFiltro('evt')">Limpiar</button>
                </div>
              </div>
              <div id="evtTabResult" style="flex:1; overflow-y:auto; padding:0.5rem; font-size:0.82rem; color:var(--text-muted); text-align:center;">
                Seleccione un activo y un rango de fechas
              </div>
            </div>
          </div>
        </div>

        <!-- Panel derecho: mapa + panel detalle overlay -->
        <div class="tracking-map-area">
          <div id="trackingMap" style="width: 100%; height: 100%; min-height: 500px;"></div>

          <!-- Panel de detalle (slide-in) -->
          <div class="tracking-detail-panel" id="detailPanel">
            <div class="tracking-detail-header">
              <h3 id="detailTitle">Detalle</h3>
              <button class="tracking-detail-close" onclick="trackingView.closeDetailPanel()">&times;</button>
            </div>
            <div class="tracking-detail-body" id="detailBody">
            </div>
          </div>
        </div>
      </div>

      ${esDistribuidor ? '' : `
      <!-- Modal Distribuidores: totales y listado de vehículos asignados -->
      <div id="distribuidoresModal" class="modal" style="display: none;">
        <div class="modal-content" style="max-width: 640px;">
          <div class="modal-header">
            <h2 class="modal-title" id="distModalTitle">Distribuidores</h2>
            <button class="modal-close" onclick="trackingView.cerrarDistribuidores()">&times;</button>
          </div>
          <div class="modal-body" id="distModalBody" style="min-height: 200px;">
            Cargando...
          </div>
        </div>
      </div>`}
      </div>
      ${ppTrk.puedeEditar ? `
      <div class="erp-panel" role="tabpanel" data-panel="formulario" id="trk_panelFormulario" hidden>
        <div class="card erp-form-card">
          <div class="card-header"><h2 class="card-title">Ligar cada GPS a su unidad</h2></div>
          <div class="erp-aviso erp-aviso-info">
            Cada vehículo de RedGPS queda ligado a <strong>una unidad</strong> (D10). Con la liga, Tracking abre la ficha de la unidad
            y el distribuidor de la unidad ve su GPS. Escribe la serie de la unidad y guarda; deja vacío y guarda para quitar la liga.
            Cada cambio queda en la bitácora de la unidad.
          </div>
          <div class="table-container">
            <table class="table">
              <thead><tr><th>Vehículo GPS</th><th>Serie en RedGPS</th><th>Unidad ligada</th><th>Ligar a la unidad</th><th></th></tr></thead>
              <tbody id="trkLigaBody"><tr><td colspan="5" class="text-center"><div class="loading-spinner"></div></td></tr></tbody>
            </table>
          </div>
          <datalist id="trkUnidadesLista"></datalist>
          <div class="erp-acciones-form"><button type="button" class="btn btn-secondary" id="trkLigaVolver">Cancelar</button></div>
        </div>
      </div>` : ''}
    `;
  },

  async afterRender() {
    if (!permisos.canView('tracking')) {
      if (permisos.primeraRuta) { router.navigate(permisos.primeraRuta); }
      else { document.getElementById('content').innerHTML = '<div style="display:flex;justify-content:center;align-items:center;height:60vh;"><div style="text-align:center;color:var(--primary);"><h2>Acceso denegado</h2><p>No tienes permisos para acceder a este módulo.</p></div></div>'; }
      return;
    }
    window.trackingView = this;
    this.tabs = new Pestanas('trk', {
      alCambiar: (nombre) => {
        if (nombre === 'formulario') this.cargarLigas();
        // El mapa estuvo oculto: que recalcule su tamaño al volver
        else if (this.data.map && window.google?.maps?.event) window.google.maps.event.trigger(this.data.map, 'resize');
      }
    });
    document.getElementById('trkLigaVolver')?.addEventListener('click', () => this.tabs.volver());

    // Pre-filtro desde URL: /admin/tracking?search=<num_serie>
    // Permite venir desde otros módulos (ej. ficha de tractor) con filtro aplicado.
    const urlParams = new URLSearchParams(window.location.search);
    const searchFromUrl = urlParams.get('search');
    if (searchFromUrl) {
      this.data.filters.search = searchFromUrl;
      const input = document.getElementById('searchInput');
      if (input) input.value = searchFromUrl;
    }

    this.setupEventListeners();
    this.initNotifCenter();

    try {
      await Promise.all([
        this.cargarStats(),
        this.cargarVehiculos(),
        this.initMap()
      ]);
      // Tras paralelo: mapa puede haber quedado listo despues de vehiculos (o al reves); un segundo pintado evita lista sin pins
      if (this.data.map) this.updateMarkers();
      await this.pollNotificaciones();
    } catch (error) {
      console.error('Error inicializando tracking:', error);
      window.toast?.error('Error al cargar el módulo de tracking');
    }

    // Auto-refresh cada 60 segundos
    this.data.refreshInterval = setInterval(() => {
      if (!document.getElementById('trackingMap')) {
        clearInterval(this.data.refreshInterval);
        if (this.data.notifPollInterval) clearInterval(this.data.notifPollInterval);
        return;
      }
      this.refreshData();
    }, 60000);

    // Polling del centro de notificaciones (misma cadencia que el push backend)
    this.data.notifPollInterval = setInterval(() => {
      if (!document.getElementById('trackingMap')) {
        clearInterval(this.data.notifPollInterval);
        return;
      }
      this.pollNotificaciones();
    }, 60000);

  },

  setupEventListeners() {
    let searchTimeout;
    document.getElementById('searchInput')?.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.closeDetailPanel();
        this.data.filters.search = e.target.value;
        this.cargarVehiculos();
      }, 500);
    });

    document.getElementById('btnDistribuidores')?.addEventListener('click', () => {
      this.abrirDistribuidores();
    });
    document.getElementById('distribuidoresModal')?.addEventListener('click', (e) => {
      if (e.target?.id === 'distribuidoresModal') this.cerrarDistribuidores();
    });

    document.getElementById('btnRefresh')?.addEventListener('click', () => {
      this.refreshData();
    });

    // Centro de notificaciones: historial de BD, abrir/cerrar y paginacion visual
    document.getElementById('btnNotifCenter')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleNotifPanel();
    });
    document.getElementById('btnNotifClose')?.addEventListener('click', () => this.closeNotifPanel());
    document.getElementById('btnNotifVerMas')?.addEventListener('click', () => this.loadMoreNotificaciones());

    // Cerrar panel al click fuera
    if (this.data._notifDocClickBound) {
      document.removeEventListener('click', this.data._notifDocClickBound);
    }
    this.data._notifDocClickBound = (e) => {
      const wrap = document.querySelector('.tracking-notif-wrap');
      if (!wrap || !this.data.notifPanelOpen) return;
      if (!wrap.contains(e.target)) this.closeNotifPanel();
    };
    document.addEventListener('click', this.data._notifDocClickBound);
  },

  // ====================== CENTRO DE NOTIFICACIONES ======================

  /** Inicializa el centro y el listener del service worker */
  initNotifCenter() {
    // Ultimo id "visto" al abrir la campana: el badge solo cuenta ids mayores
    try {
      const raw = localStorage.getItem('tracking.notif.lastSeenId');
      const n = raw != null ? Number(raw) : 0;
      this.data.notifLastSeenId = Number.isFinite(n) ? n : 0;
    } catch (e) {
      this.data.notifLastSeenId = 0;
    }

    // Escuchar click en push del SO para seleccionar vehiculo
    if (this.data._notifSwBound && navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener('message', this.data._notifSwBound);
    }
    this.data._notifSwBound = (event) => {
      if (event.data?.type !== 'PUSH_NOTIFICATION_CLICK') return;
      const d = event.data.data || {};
      this.ingestPushNotificacion(d);
      this.handleNotifClickFromData(d);
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', this.data._notifSwBound);
    }

    this.renderNotifPanel();
  },

  /**
   * Clasifica visualmente: ubicacion (azul), evento (amarillo), alerta (rojo)
   */
  _resolveNotifVisual(n) {
    const tipo = String(n?.tipo || '').toLowerCase();
    const evento = String(n?.evento || '').toLowerCase();
    const motivo = String(n?.motivo_notificacion || '').toLowerCase();

    if (
      tipo === 'ubicacion' ||
      evento.includes('ubicacion') ||
      evento.includes('posicion') ||
      motivo.includes('ubicacion')
    ) {
      return { key: 'ubicacion', label: 'Ubicacion', icon: '&#128205;' };
    }
    if (
      tipo === 'alerta' ||
      motivo.startsWith('telemetria') ||
      evento.includes('alerta') ||
      evento.includes('alarma') ||
      evento.includes('panico') ||
      evento.includes('pánico')
    ) {
      return { key: 'alerta', label: 'Alerta', icon: '&#9888;' };
    }
    return { key: 'evento', label: 'Evento', icon: '&#128203;' };
  },

  /** Hora corta para el pie de la tarjeta (HH:mm) */
  _formatNotifHoraCorta(fecha) {
    if (!fecha) return '';
    const d = new Date(fecha);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  },

  /** Marca el lote actual como visto: el badge se limpia hasta que lleguen nuevas */
  markNotificacionesVistas() {
    const maxId = this.data.notificaciones.reduce((acc, n) => {
      const id = Number(n.id);
      return Number.isFinite(id) && id > acc ? id : acc;
    }, this.data.notifLastSeenId || 0);
    this.data.notifLastSeenId = maxId;
    try {
      localStorage.setItem('tracking.notif.lastSeenId', String(maxId));
    } catch (e) { /* ignore */ }
    this.updateNotifBadge();
  },

  /** Badge = cantidad de notificaciones con id mayor al ultimo visto */
  updateNotifBadge() {
    const badge = document.getElementById('notifBadge');
    if (!badge) return;
    const lastSeen = Number(this.data.notifLastSeenId) || 0;
    const pendientes = this.data.notificaciones.filter((n) => {
      const id = Number(n.id);
      return Number.isFinite(id) && id > lastSeen;
    }).length;
    badge.textContent = pendientes > 99 ? '99+' : String(pendientes);
    badge.classList.toggle('visible', pendientes > 0);
  },

  /**
   * Normaliza una notificacion del API o del SW a la forma del buffer
   */
  normalizeNotificacion(raw) {
    if (!raw) return null;
    const id = raw.id != null ? String(raw.id) : `tmp-${raw.vehiculoId || raw.vehiculo_id || ''}-${raw.fecha_hora || Date.now()}`;
    const tipo = raw.tipo || 'evento';
    return {
      id,
      tipo,
      evento: raw.evento || raw.event_label || 'Evento',
      vehiculo_id: raw.vehiculo_id ?? raw.vehiculoId ?? null,
      imei: raw.imei || null,
      nombre_activo: raw.nombre_activo || raw.vehiculo || '',
      placa: raw.placa || '',
      fecha_hora: raw.fecha_hora || raw.recibido_en || null,
      recibido_en: raw.recibido_en || raw.fecha_hora || null,
      latitude: raw.latitude ?? raw.lat ?? null,
      longitude: raw.longitude ?? raw.lng ?? null,
      direccion: raw.direccion || null,
      motivo_notificacion: raw.motivo_notificacion || null
    };
  },

  /** Inserta o actualiza en el buffer FIFO (max 200), mas reciente primero */
  upsertNotificaciones(items) {
    if (!items?.length) return;
    const byId = new Map(this.data.notificaciones.map(n => [String(n.id), n]));
    for (const raw of items) {
      const n = this.normalizeNotificacion(raw);
      if (!n) continue;
      byId.set(String(n.id), n);
    }
    const merged = [...byId.values()].sort((a, b) => {
      const ta = new Date(a.recibido_en || a.fecha_hora || 0).getTime();
      const tb = new Date(b.recibido_en || b.fecha_hora || 0).getTime();
      return tb - ta;
    });
    this.data.notificaciones = merged.slice(0, 200);
    this.renderNotifPanel();
  },

  /** Notificacion llegada desde el SW (push click o push recibido) */
  ingestPushNotificacion(data) {
    if (!data) return;
    this.upsertNotificaciones([{
      id: data.id || `push-${Date.now()}`,
      tipo: data.tipo || 'evento',
      evento: data.evento,
      vehiculo_id: data.vehiculoId || data.vehiculo_id,
      imei: data.imei,
      nombre_activo: data.vehiculo,
      placa: data.placa,
      fecha_hora: data.fecha_hora,
      latitude: data.lat,
      longitude: data.lng,
      direccion: data.direccion
    }]);
  },

  async pollNotificaciones() {
    try {
      // Sin cursor de sesion: siempre consulta el historial reciente persistido en BD.
      const response = await trackingService.getNotificacionesNuevas();
      const list = response.data?.notificaciones || response.notificaciones || [];
      this.upsertNotificaciones(list);
    } catch (error) {
      console.error('Error cargando notificaciones:', error);
    }
  },

  toggleNotifPanel() {
    if (this.data.notifPanelOpen) this.closeNotifPanel();
    else this.openNotifPanel();
  },

  openNotifPanel() {
    this.data.notifPanelOpen = true;
    document.getElementById('notifPanel')?.classList.add('open');
    this.renderNotifPanel();
    // Al abrir se consideran vistas: el contador del badge desaparece
    this.markNotificacionesVistas();
    // Refrescar desde BD al abrir para mostrar los datos mas recientes.
    this.pollNotificaciones().then(() => {
      // Si llegaron filas nuevas mientras se abria, tambien marcarlas vistas
      if (this.data.notifPanelOpen) this.markNotificacionesVistas();
    });
  },

  closeNotifPanel() {
    this.data.notifPanelOpen = false;
    document.getElementById('notifPanel')?.classList.remove('open');
    // Asegurar que al cerrar el badge quede limpio respecto al lote mostrado
    this.markNotificacionesVistas();
  },

  loadMoreNotificaciones() {
    if (this.data.notifLimit < 25) this.data.notifLimit = 25;
    else if (this.data.notifLimit < 50) this.data.notifLimit = 50;
    else this.data.notifLimit = Math.min(this.data.notifLimit + 25, 200);
    this.renderNotifPanel();
  },

  /** Escapa texto para insertar en HTML del panel de notificaciones */
  _escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  },

  renderNotifPanel() {
    this.updateNotifBadge();

    const listEl = document.getElementById('notifList');
    if (!listEl) return;

    const items = this.data.notificaciones;
    const visible = items.slice(0, this.data.notifLimit);

    if (visible.length === 0) {
      listEl.innerHTML = '<div class="tracking-notif-empty">Sin notificaciones recientes en la base de datos</div>';
      return;
    }

    listEl.innerHTML = visible.map((n) => {
      const visual = this._resolveNotifVisual(n);
      const serie = n.nombre_activo || 'Activo';
      const placa = n.placa ? ` (${n.placa})` : '';
      const fecha = trackingService.formatFechaHora(n.fecha_hora || n.recibido_en);
      const hora = this._formatNotifHoraCorta(n.fecha_hora || n.recibido_en);
      const eventoTxt = n.evento || visual.label;
      const dir = n.direccion
        ? `<div class="tracking-notif-item-desc">${this._escapeHtml(n.direccion)}</div>`
        : '';
      return `
        <button type="button" class="tracking-notif-item tipo-${visual.key}" data-notif-id="${this._escapeHtml(n.id)}">
          <div class="tracking-notif-rail" aria-hidden="true">
            <span class="tracking-notif-rail-icon">${visual.icon}</span>
          </div>
          <div class="tracking-notif-item-body">
            <div class="tracking-notif-item-title">
              <span class="tracking-notif-serie">${this._escapeHtml(serie)}</span>${this._escapeHtml(placa)}
              <span class="tracking-notif-sep"> - </span>
              <span>${this._escapeHtml(eventoTxt)}</span>
            </div>
            <div class="tracking-notif-item-desc">
              ${this._escapeHtml(hora || fecha)} ${this._escapeHtml(serie)} genero ${this._escapeHtml(visual.label.toLowerCase())} ${this._escapeHtml(eventoTxt)}
            </div>
            ${dir}
            <div class="tracking-notif-item-footer">
              <span class="tracking-notif-item-time">${this._escapeHtml(hora || fecha)}</span>
              <span class="tracking-notif-pill tipo-${visual.key}">${visual.label}</span>
            </div>
          </div>
        </button>
      `;
    }).join('');

    // Delegacion de click (evita problemas de escapado en ids)
    listEl.onclick = (e) => {
      const btn = e.target.closest('[data-notif-id]');
      if (!btn) return;
      this.onNotifItemClick(btn.getAttribute('data-notif-id'));
    };

    const btnMore = document.getElementById('btnNotifVerMas');
    if (btnMore) {
      btnMore.style.display = items.length > this.data.notifLimit ? '' : 'none';
    }
  },

  async onNotifItemClick(notifId) {
    const n = this.data.notificaciones.find(x => String(x.id) === String(notifId));
    if (!n) return;
    this.closeNotifPanel();
    await this.selectVehicleFromNotif(n);
  },

  async handleNotifClickFromData(data) {
    const n = this.normalizeNotificacion({
      id: data.id || `push-${Date.now()}`,
      tipo: data.tipo,
      evento: data.evento,
      vehiculo_id: data.vehiculoId || data.vehiculo_id,
      imei: data.imei,
      nombre_activo: data.vehiculo,
      placa: data.placa,
      fecha_hora: data.fecha_hora,
      latitude: data.lat,
      longitude: data.lng,
      direccion: data.direccion
    });
    this.closeNotifPanel();
    await this.selectVehicleFromNotif(n);
  },

  /**
   * Misma accion que click en dispositivo: selectVehicle.
   * Si no esta en la lista actual (filtro/pagina), limpia a Total y busca.
   */
  async selectVehicleFromNotif(n) {
    if (!n) return;

    let vehicleId = n.vehiculo_id != null ? Number(n.vehiculo_id) : null;
    let found = vehicleId
      ? this.data.vehiculos.find(v => Number(v.id) === vehicleId)
      : null;

    if (!found && (n.imei || n.placa || n.nombre_activo)) {
      found = this.data.vehiculos.find(v =>
        (n.imei && String(v.imei) === String(n.imei)) ||
        (n.placa && String(v.placa) === String(n.placa)) ||
        (n.nombre_activo && (
          trackingService.etiquetaVehiculo(v) === n.nombre_activo ||
          v.nombre_activo === n.nombre_activo
        ))
      );
      if (found) vehicleId = found.id;
    }

    // No esta en la vista filtrada: reset a Total y buscar de nuevo
    if (!found) {
      if (this.data.activeStatFilter || this.data.filters.search) {
        this.data.activeStatFilter = null;
        this.data.filters.estado = '';
        this.data.filters.search = '';
        const input = document.getElementById('searchInput');
        if (input) input.value = '';
        document.querySelectorAll('.tracking-stat-chip').forEach(chip => chip.classList.remove('chip-active'));
        document.getElementById('statChipTotal')?.classList.add('chip-active');
        await this.cargarVehiculos();
      }

      found = vehicleId
        ? this.data.vehiculos.find(v => Number(v.id) === vehicleId)
        : this.data.vehiculos.find(v =>
          (n.imei && String(v.imei) === String(n.imei)) ||
          (n.placa && String(v.placa) === String(n.placa)) ||
          (n.nombre_activo && (
            trackingService.etiquetaVehiculo(v) === n.nombre_activo ||
            v.nombre_activo === n.nombre_activo
          ))
        );
      if (found) vehicleId = found.id;
    }

    if (!vehicleId && !found) {
      // Ultimo recurso: buscar por API con placa/serie
      const q = n.placa || n.nombre_activo;
      if (q) {
        try {
          const response = await trackingService.getVehiculos({ search: q, limit: 10 });
          const list = response.data || [];
          const match = list.find(v =>
            (n.imei && String(v.imei) === String(n.imei)) ||
            (n.placa && String(v.placa) === String(n.placa)) ||
            (n.nombre_activo && trackingService.etiquetaVehiculo(v) === n.nombre_activo)
          ) || list[0];
          if (match) {
            this.data.filters.search = q;
            const input = document.getElementById('searchInput');
            if (input) input.value = q;
            await this.cargarVehiculos();
            vehicleId = match.id;
            found = this.data.vehiculos.find(v => Number(v.id) === Number(match.id)) || match;
          }
        } catch (e) {
          console.error('Error resolviendo vehiculo de notificacion:', e);
        }
      }
    }

    if (vehicleId || found) {
      this.selectVehicle(vehicleId || found.id, true);
      // Abrir info window del marker si existe
      const mid = String(vehicleId || found.id);
      const marker = this.data.markers[mid];
      const veh = this.data.vehiculos.find(v => Number(v.id) === Number(mid));
      if (marker && veh) this.showInfoWindow(veh, marker);
    }
  },

  // --- Tabs ---
  switchTab(tab) {
    this.data.activeTab = tab;
    document.querySelectorAll('.tracking-tab').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tab === tab);
    });
    document.querySelectorAll('.tracking-tab-pane').forEach(pane => {
      pane.classList.toggle('active', pane.id === `pane-${tab}`);
    });
    // Poblar listas de vehículos en tabs
    if (tab === 'recorridos') this.renderTabVehicleList('rec');
    if (tab === 'alertas') this.renderTabVehicleList('alt');
    if (tab === 'eventos') this.renderTabVehicleList('evt');
  },

  renderTabVehicleList(prefix) {
    const container = document.getElementById(`${prefix}VehicleList`);
    if (!container) return;

    const hoy = this.hoyLocal();
    const fechaInicioEl = document.getElementById(`${prefix}FechaInicio`);
    const fechaFinEl = document.getElementById(`${prefix}FechaFin`);
    if (fechaInicioEl && !fechaInicioEl.value) fechaInicioEl.value = hoy;
    if (fechaFinEl && !fechaFinEl.value) fechaFinEl.value = hoy;

    // Filtros
    const filterPlaca = document.getElementById(`${prefix}FilterPlaca`)?.value?.toLowerCase() || '';
    const filterNombre = document.getElementById(`${prefix}FilterNombre`)?.value?.toLowerCase() || '';
    const filterDistribuidor = document.getElementById(`${prefix}FilterDistribuidor`)?.value?.toLowerCase() || '';

    const vehiculos = this.data.vehiculos.filter(v => {
      if (filterPlaca && !v.placa.toLowerCase().includes(filterPlaca)) return false;
      if (filterNombre) {
        const q = filterNombre.toLowerCase();
        const label = trackingService.etiquetaVehiculo(v).toLowerCase();
        const nombreGps = (v.nombre_activo || '').toLowerCase();
        if (!label.includes(q) && !nombreGps.includes(q)) return false;
      }
      if (filterDistribuidor && !(v.distribuidor_nombre || '').toLowerCase().includes(filterDistribuidor)) return false;
      return true;
    });

    container.innerHTML = `
      <table style="width:100%; font-size:0.78rem; border-collapse:collapse;">
        <thead>
          <tr style="background:var(--gray-50);">
            <th style="padding:0.3rem 0.4rem; text-align:left; width:24px;">
              <input type="checkbox" id="${prefix}-select-all" onclick="trackingView.toggleAllTabVehicles('${prefix}', this.checked)" title="Seleccionar todos">
            </th>
            <th style="padding:0.3rem 0.4rem; text-align:left;">Serie</th>
            <th style="padding:0.3rem 0.4rem; text-align:left;">Placa</th>
            <th style="padding:0.3rem 0.4rem; text-align:left;">Equipo</th>
          </tr>
        </thead>
        <tbody>
          ${vehiculos.map(v => `
            <tr style="border-bottom:1px solid var(--gray-100); cursor:pointer;"
                onclick="trackingView.toggleTabVehicle('${prefix}', ${v.id}, this)">
              <td style="padding:0.25rem 0.4rem;"><input type="checkbox" class="${prefix}-vehicle-check" value="${v.id}" style="pointer-events:none;"></td>
              <td style="padding:0.25rem 0.4rem;">${trackingService.etiquetaVehiculo(v)}</td>
              <td style="padding:0.25rem 0.4rem;">${v.placa}</td>
              <td style="padding:0.25rem 0.4rem; font-size:0.7rem; color:var(--gray-500);">${v.imei || '-'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;

    // Setup filter listeners
    const setupFilter = (id) => {
      const el = document.getElementById(id);
      if (el && !el.dataset.listening) {
        el.dataset.listening = '1';
        el.addEventListener('input', () => this.renderTabVehicleList(prefix));
      }
    };
    setupFilter(`${prefix}FilterPlaca`);
    setupFilter(`${prefix}FilterNombre`);
    setupFilter(`${prefix}FilterDistribuidor`);
  },

  toggleTabVehicle(prefix, vehicleId, row) {
    const checkbox = row.querySelector(`.${prefix}-vehicle-check`);
    if (checkbox) checkbox.checked = !checkbox.checked;
    // Actualizar estado del "Todos"
    const allCheck = document.getElementById(`${prefix}-select-all`);
    if (allCheck) {
      const total = document.querySelectorAll(`.${prefix}-vehicle-check`).length;
      const checked = document.querySelectorAll(`.${prefix}-vehicle-check:checked`).length;
      allCheck.checked = total > 0 && checked === total;
    }
  },

  toggleAllTabVehicles(prefix, checked) {
    document.querySelectorAll(`.${prefix}-vehicle-check`).forEach(cb => {
      cb.checked = checked;
    });
  },

  getSelectedTabVehicles(prefix) {
    const checks = document.querySelectorAll(`.${prefix}-vehicle-check:checked`);
    return Array.from(checks).map(c => parseInt(c.value));
  },

  async verRecorridosTab() {
    const ids = this.getSelectedTabVehicles('rec');
    if (ids.length === 0) {
      window.toast?.warning('Seleccione al menos un activo');
      return;
    }
    const fechaInicio = document.getElementById('recFechaInicio')?.value;
    const fechaFin = document.getElementById('recFechaFin')?.value;
    if (!fechaInicio || !fechaFin) {
      window.toast?.warning('Seleccione rango de fechas');
      return;
    }

    const result = document.getElementById('recTabResult');
    if (result) result.innerHTML = '<div style="padding:1rem; text-align:center;">Cargando recorridos...</div>';

    // Limpiar rutas anteriores
    this.clearTabRoutes();

    const colors = ['#1a73e8', '#e8341a', '#28a745', '#ff9800', '#9c27b0', '#00bcd4'];
    let allBounds = new google.maps.LatLngBounds();
    let hasData = false;
    let summaryHtml = '';

    for (let i = 0; i < ids.length; i++) {
      const vehiculo = this.data.vehiculos.find(v => v.id === ids[i]);
      if (!vehiculo) continue;

      try {
        const response = await trackingService.getRecorridos(ids[i], fechaInicio, fechaFin);
        const recorridos = response.data?.recorridos || [];
        const resumen = response.data?.resumen || {};

        if (recorridos.length === 0) {
          summaryHtml += `<div style="padding:0.3rem 0; color:var(--text-muted);">${trackingService.etiquetaVehiculo(vehiculo)}: sin datos</div>`;
          continue;
        }

        hasData = true;
        const color = colors[i % colors.length];
        const path = recorridos.filter(r => r.latitude && r.longitude).map(r => ({ lat: r.latitude, lng: r.longitude }));

        if (path.length > 0) {
          const polyline = new google.maps.Polyline({
            path, geodesic: true, strokeColor: color, strokeOpacity: 0.8, strokeWeight: 3, map: this.data.map
          });
          if (!this.data.tabRoutes) this.data.tabRoutes = [];
          this.data.tabRoutes.push(polyline);

          // Marcadores inicio/fin
          const mkStart = new google.maps.Marker({
            position: path[0], map: this.data.map,
            label: { text: 'A', color: '#fff', fontSize: '10px', fontWeight: 'bold' },
            icon: { path: google.maps.SymbolPath.CIRCLE, fillColor: color, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2, scale: 9 }
          });
          const mkEnd = new google.maps.Marker({
            position: path[path.length - 1], map: this.data.map,
            label: { text: 'B', color: '#fff', fontSize: '10px', fontWeight: 'bold' },
            icon: { path: google.maps.SymbolPath.CIRCLE, fillColor: color, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2, scale: 9 }
          });
          this.data.tabRoutes.push(mkStart, mkEnd);
          path.forEach(p => allBounds.extend(p));
        }

        const val = (v, s) => (v !== undefined && v !== null) ? `${v}${s || ''}` : '-';
        summaryHtml += `
          <div style="border-bottom:1px solid var(--gray-100); padding:0.5rem 0;">
            <div style="font-weight:600; font-size:0.82rem;"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:4px;"></span>${trackingService.etiquetaVehiculo(vehiculo)}</div>
            <div style="font-size:0.78rem; color:var(--gray-600); display:grid; grid-template-columns:1fr 1fr; gap:0.1rem 0.5rem; margin-top:0.25rem;">
              <span>Puntos: ${recorridos.length}</span>
              <span>Vel. max: ${val(resumen.velocidad_maxima, ' km/h')}</span>
              <span>Movimiento: ${val(resumen.tiempo_movimiento)}</span>
              <span>Detenido: ${val(resumen.tiempo_detenido)}</span>
              <span>Motor ON: ${val(resumen.tiempo_motor_encendido)}</span>
              ${resumen.distancia_odometro ? `<span>Dist: ${resumen.distancia_odometro} km</span>` : ''}
            </div>
          </div>
        `;
      } catch (error) {
        summaryHtml += `<div style="padding:0.3rem 0; color:var(--danger);">${trackingService.etiquetaVehiculo(vehiculo)}: error</div>`;
      }
    }

    if (result) {
      result.innerHTML = hasData ? summaryHtml : '<div style="padding:1rem; text-align:center; color:var(--text-muted);">No se encontraron recorridos</div>';
    }

    if (hasData && this.data.map) {
      this.data.map.fitBounds(allBounds, { padding: 50 });
    }
  },

  quitarRecorridosTab() {
    this.clearTabRoutes();
    // Reset checkboxes
    document.querySelectorAll('.rec-vehicle-check').forEach(c => { c.checked = false; });
    // Reset fechas a hoy
    const hoy = this.hoyLocal();
    const fi = document.getElementById('recFechaInicio');
    const ff = document.getElementById('recFechaFin');
    if (fi) fi.value = hoy;
    if (ff) ff.value = hoy;
    // Reset filtros
    const fp = document.getElementById('recFilterPlaca');
    const fn = document.getElementById('recFilterNombre');
    const fd = document.getElementById('recFilterDistribuidor');
    if (fp) fp.value = '';
    if (fn) fn.value = '';
    if (fd) fd.value = '';
    this.renderTabVehicleList('rec');
    const result = document.getElementById('recTabResult');
    if (result) result.innerHTML = '<div style="text-align:center; color:var(--text-muted);">Seleccione uno o m&aacute;s activos y un rango de fechas</div>';
  },

  clearTabRoutes() {
    if (this.data.tabRoutes) {
      this.data.tabRoutes.forEach(item => item.setMap(null));
      this.data.tabRoutes = [];
    }
  },

  async verEventosTab() {
    const ids = this.getSelectedTabVehicles('evt');
    if (ids.length === 0) {
      window.toast?.warning('Seleccione al menos un activo');
      return;
    }
    const fechaInicio = document.getElementById('evtFechaInicio')?.value;
    const fechaFin = document.getElementById('evtFechaFin')?.value;
    if (!fechaInicio || !fechaFin) {
      window.toast?.warning('Seleccione rango de fechas');
      return;
    }

    const result = document.getElementById('evtTabResult');
    if (result) result.innerHTML = '<div style="padding:1rem; text-align:center;">Cargando eventos...</div>';

    let allEventos = [];

    for (const id of ids) {
      const vehiculo = this.data.vehiculos.find(v => v.id === id);
      if (!vehiculo) continue;
      try {
        const response = await trackingService.getEventos(id, fechaInicio, fechaFin);
        const eventos = response.data?.eventos || [];
        eventos.forEach(e => { e._vehiculo = vehiculo; });
        allEventos = allEventos.concat(eventos);
      } catch (error) {
        console.error(`Error cargando eventos para ${id}:`, error);
      }
    }

    // Ordenar por fecha
    allEventos.sort((a, b) => new Date(b.fecha_hora) - new Date(a.fecha_hora));

    // Guardar para acciones
    this.data.tabEventos = allEventos;

    if (allEventos.length === 0) {
      if (result) result.innerHTML = '<div style="padding:1rem; text-align:center; color:var(--text-muted);">No se encontraron eventos</div>';
      return;
    }

    if (result) {
      result.innerHTML = `
        <table class="tracking-events-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Activo</th>
              <th>Evento</th>
              <th style="width:50px; text-align:center;">Acci&oacute;n</th>
            </tr>
          </thead>
          <tbody>
            ${allEventos.map((e, i) => `
              <tr>
                <td>${trackingService.formatFechaHora(e.fecha_hora)}</td>
                <td style="font-size:0.75rem;">${trackingService.etiquetaVehiculo(e._vehiculo)}</td>
                <td>${e.evento}</td>
                <td style="text-align:center; white-space:nowrap;">
                  ${e.latitude ? `<button class="tracking-action-btn" title="Ubicar" onclick="trackingView.ubicarTabEvento(${i})">&#x1F4CD;</button>` : ''}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    }
  },

  async verAlertasTab() {
    const ids = this.getSelectedTabVehicles('alt');
    if (ids.length === 0) {
      window.toast?.warning('Seleccione al menos un activo');
      return;
    }
    const fechaInicio = document.getElementById('altFechaInicio')?.value;
    const fechaFin = document.getElementById('altFechaFin')?.value;
    if (!fechaInicio || !fechaFin) {
      window.toast?.warning('Seleccione rango de fechas');
      return;
    }

    const result = document.getElementById('altTabResult');
    if (result) result.innerHTML = '<div style="padding:1rem; text-align:center;">Cargando alertas...</div>';

    let allAlertas = [];

    for (const id of ids) {
      const vehiculo = this.data.vehiculos.find(v => v.id === id);
      if (!vehiculo) continue;
      try {
        const response = await trackingService.getAlertas(id, fechaInicio, fechaFin);
        const alertas = response.data?.alertas || [];
        alertas.forEach(a => { a._vehiculo = vehiculo; });
        allAlertas = allAlertas.concat(alertas);
      } catch (error) {
        console.error(`Error cargando alertas para ${id}:`, error);
      }
    }

    allAlertas.sort((a, b) => new Date(b.fecha_hora) - new Date(a.fecha_hora));
    this.data.tabAlertas = allAlertas;

    if (allAlertas.length === 0) {
      if (result) result.innerHTML = '<div style="padding:1rem; text-align:center; color:var(--text-muted);">No se encontraron alertas en este rango</div>';
      return;
    }

    if (result) {
      result.innerHTML = `
        <table class="tracking-events-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Activo</th>
              <th>Alerta</th>
              <th style="width:50px; text-align:center;">Acci\u00f3n</th>
            </tr>
          </thead>
          <tbody>
            ${allAlertas.map((a, i) => `
              <tr>
                <td>${trackingService.formatFechaHora(a.fecha_hora)}</td>
                <td style="font-size:0.75rem;">${trackingService.etiquetaVehiculo(a._vehiculo)}</td>
                <td>${a.evento}</td>
                <td style="text-align:center; white-space:nowrap;">
                  ${a.latitude ? `<button class="tracking-action-btn" title="Ubicar" onclick="trackingView.ubicarTabAlerta(${i})">&#x1F4CD;</button>` : ''}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    }
  },

  limpiarTabFiltro(prefix) {
    // Deseleccionar checkboxes
    document.querySelectorAll(`.${prefix}-vehicle-check`).forEach(c => { c.checked = false; });
    // Limpiar filtros de texto
    const placa = document.getElementById(`${prefix}FilterPlaca`);
    const nombre = document.getElementById(`${prefix}FilterNombre`);
    const distribuidor = document.getElementById(`${prefix}FilterDistribuidor`);
    if (placa) placa.value = '';
    if (nombre) nombre.value = '';
    if (distribuidor) distribuidor.value = '';
    // Reset fechas a hoy
    const hoy = this.hoyLocal();
    const fi = document.getElementById(`${prefix}FechaInicio`);
    const ff = document.getElementById(`${prefix}FechaFin`);
    if (fi) fi.value = hoy;
    if (ff) ff.value = hoy;
    // Limpiar resultado
    const resultId = prefix === 'alt' ? 'altTabResult' : 'evtTabResult';
    const result = document.getElementById(resultId);
    if (result) result.innerHTML = '<div style="padding:1rem; text-align:center; color:var(--text-muted);">Seleccione un activo y un rango de fechas</div>';
    // Re-render lista
    this.renderTabVehicleList(prefix);
  },

  // --- Modal Distribuidores (solo roles sin scope) ---

  async abrirDistribuidores() {
    const modal = document.getElementById('distribuidoresModal');
    if (!modal) return;
    modal.style.display = 'flex';
    const body = document.getElementById('distModalBody');
    const title = document.getElementById('distModalTitle');
    if (title) title.textContent = 'Distribuidores';
    if (body) body.innerHTML = '<div style="padding:1rem; text-align:center; color:var(--text-muted);">Cargando distribuidores...</div>';

    try {
      const response = await trackingService.getDistribuidores();
      this.data.distribuidores = response.data?.distribuidores || [];
      this.data.distFiltro = '';
      this.renderDistribuidoresTabla();
    } catch (error) {
      console.error('Error cargando distribuidores:', error);
      if (body) body.innerHTML = '<div style="padding:1rem; text-align:center; color:var(--danger, #dc3545);">Error cargando distribuidores</div>';
    }
  },

  renderDistribuidoresTabla() {
    const body = document.getElementById('distModalBody');
    if (!body) return;
    const q = (this.data.distFiltro || '').toLowerCase();
    const lista = (this.data.distribuidores || []).filter(d =>
      !q || (d.nombre || '').toLowerCase().includes(q)
    );

    body.innerHTML = `
      <div style="margin-bottom:0.75rem; display:flex; justify-content:flex-end;">
        <input type="text" id="distSearchInput" class="form-control" placeholder="Buscar distribuidor..."
               value="${this.data.distFiltro || ''}" style="max-width:240px; font-size:0.85rem;">
      </div>
      <table class="table" style="width:100%; font-size:0.85rem;">
        <thead>
          <tr>
            <th style="text-align:left;">Nombre</th>
            <th style="text-align:center;">Total asignados</th>
            <th style="text-align:center; width:80px;">Acciones</th>
          </tr>
        </thead>
        <tbody>
          ${lista.length === 0
            ? '<tr><td colspan="3" style="text-align:center; color:var(--text-muted); padding:1rem;">Sin distribuidores</td></tr>'
            : lista.map(d => `
              <tr>
                <td>${d.nombre || '-'}</td>
                <td style="text-align:center;">${d.total_asignados}</td>
                <td style="text-align:center;">
                  <button class="tracking-action-btn" title="Ver veh&iacute;culos asignados"
                          onclick="trackingView.verVehiculosDistribuidor(${d.id}, this.dataset.nombre)"
                          data-nombre="${(d.nombre || '').replace(/"/g, '&quot;')}">&#x1F441;</button>
                </td>
              </tr>
            `).join('')}
        </tbody>
      </table>
    `;

    // Filtro con debounce corto; se re-renderiza la tabla conservando el foco
    const input = document.getElementById('distSearchInput');
    if (input) {
      input.addEventListener('input', (e) => {
        clearTimeout(this._distSearchTimeout);
        this._distSearchTimeout = setTimeout(() => {
          this.data.distFiltro = e.target.value;
          this.renderDistribuidoresTabla();
          const el = document.getElementById('distSearchInput');
          if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
        }, 250);
      });
    }
  },

  async verVehiculosDistribuidor(distribuidorId, nombre) {
    const body = document.getElementById('distModalBody');
    const title = document.getElementById('distModalTitle');
    if (title) title.textContent = `Vehículos asignados - ${nombre || ''}`;
    if (body) body.innerHTML = '<div style="padding:1rem; text-align:center; color:var(--text-muted);">Cargando veh&iacute;culos...</div>';

    try {
      const response = await trackingService.getVehiculos({ distribuidor_id: distribuidorId, limit: 100 });
      const vehiculos = response.data || [];

      if (body) {
        body.innerHTML = `
          <div style="margin-bottom:0.75rem;">
            <button class="btn btn-secondary btn-sm" onclick="trackingView.volverDistribuidores()">&larr; Volver</button>
          </div>
          ${vehiculos.length === 0
            ? '<div style="padding:1rem; text-align:center; color:var(--text-muted);">Este distribuidor no tiene veh&iacute;culos asignados</div>'
            : `
          <table class="table" style="width:100%; font-size:0.85rem;">
            <thead>
              <tr>
                <th style="text-align:left;">Serie</th>
                <th style="text-align:left;">Placa</th>
                <th style="text-align:left;">&Uacute;ltimo reporte</th>
                <th style="text-align:center; width:70px;">Acciones</th>
              </tr>
            </thead>
            <tbody>
              ${vehiculos.map(v => `
                <tr>
                  <td>${trackingService.etiquetaVehiculo(v)}</td>
                  <td>${v.placa || '-'}</td>
                  <td>${trackingService.formatFechaHora(v.fecha_hora)}</td>
                  <td style="text-align:center;">
                    <button class="tracking-action-btn" title="Ubicar en mapa" onclick="trackingView.ubicarDesdeDistribuidor(${v.id})">&#x1F4CD;</button>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>`}
        `;
      }
    } catch (error) {
      console.error('Error cargando vehículos del distribuidor:', error);
      if (body) body.innerHTML = '<div style="padding:1rem; text-align:center; color:var(--danger, #dc3545);">Error cargando veh&iacute;culos</div>';
    }
  },

  volverDistribuidores() {
    const title = document.getElementById('distModalTitle');
    if (title) title.textContent = 'Distribuidores';
    this.renderDistribuidoresTabla();
  },

  ubicarDesdeDistribuidor(vehiculoId) {
    this.cerrarDistribuidores();
    this.selectVehicle(vehiculoId);
  },

  cerrarDistribuidores() {
    const modal = document.getElementById('distribuidoresModal');
    if (modal) modal.style.display = 'none';
  },

  ubicarTabAlerta(index) {
    const alerta = this.data.tabAlertas?.[index];
    if (!alerta || !alerta.latitude || !alerta.longitude || !this.data.map) return;
    const position = { lat: alerta.latitude, lng: alerta.longitude };
    this.data.map.panTo(position);
    this.data.map.setZoom(16);
    if (this.data.infoWindow) {
      this.data.infoWindow.setContent(`
        <div style="font-family:inherit; padding:4px; min-width:180px;">
          <div style="font-weight:bold; font-size:13px;">${trackingService.etiquetaVehiculo(alerta._vehiculo)}</div>
          <div style="font-size:12px; color:var(--danger);"><strong>${alerta.evento}</strong></div>
          <div style="font-size:11px; color:#666;">${trackingService.formatFechaHora(alerta.fecha_hora)}</div>
          ${alerta.direccion ? `<div style="font-size:11px; color:#666;">${alerta.direccion}</div>` : ''}
        </div>
      `);
      this.data.infoWindow.setPosition(position);
      this.data.infoWindow.open(this.data.map);
    }
  },

  ubicarTabEvento(index) {
    const evento = this.data.tabEventos?.[index];
    if (!evento || !evento.latitude || !evento.longitude || !this.data.map) return;
    const position = { lat: evento.latitude, lng: evento.longitude };
    this.data.map.panTo(position);
    this.data.map.setZoom(16);
    if (this.data.infoWindow) {
      this.data.infoWindow.setContent(`
        <div style="font-family:inherit; padding:4px; min-width:180px;">
          <div style="font-weight:bold; font-size:13px;">${trackingService.etiquetaVehiculo(evento._vehiculo)}</div>
          <div style="font-size:12px;"><strong>${evento.evento}</strong></div>
          <div style="font-size:11px; color:#666;">${trackingService.formatFechaHora(evento.fecha_hora)}</div>
          ${evento.direccion ? `<div style="font-size:11px; color:#666;">${evento.direccion}</div>` : ''}
        </div>
      `);
      this.data.infoWindow.setPosition(position);
      this.data.infoWindow.open(this.data.map);
    }
  },

  // --- Stats como filtro ---
  filterByStat(estado) {
    // Cerrar panel de detalle al cambiar filtro
    this.closeDetailPanel();

    // Toggle: si ya está activo, quitar filtro
    if (this.data.activeStatFilter === estado) {
      this.data.activeStatFilter = null;
      this.data.filters.estado = '';
    } else {
      this.data.activeStatFilter = estado;
      this.data.filters.estado = estado || '';
    }

    // Actualizar visual de chips
    document.querySelectorAll('.tracking-stat-chip').forEach(chip => {
      chip.classList.remove('chip-active');
    });
    if (this.data.activeStatFilter) {
      const chipMap = { encendido: 'statChipEncendidos', apagado: 'statChipApagados', movimiento: 'statChipMovimiento' };
      const chipId = chipMap[this.data.activeStatFilter];
      if (chipId) document.getElementById(chipId)?.classList.add('chip-active');
    } else {
      document.getElementById('statChipTotal')?.classList.add('chip-active');
    }

    this.cargarVehiculos();
  },

  async refreshData() {
    await Promise.all([
      this.cargarStats(),
      this.cargarVehiculos()
    ]);
    this.actualizarHoraUpdate();
  },

  async cargarStats() {
    try {
      const response = await trackingService.getStats();
      const stats = response.data.stats;
      this.data.stats = stats;

      const el = (id) => document.getElementById(id);
      if (el('statTotal')) el('statTotal').textContent = stats.total;
      if (el('statEncendidos')) el('statEncendidos').textContent = stats.encendidos;
      if (el('statApagados')) el('statApagados').textContent = stats.apagados;
      if (el('statMovimiento')) el('statMovimiento').textContent = stats.en_movimiento;
    } catch (error) {
      console.error('Error cargando stats:', error);
    }
  },

  async cargarVehiculos() {
    try {
      const params = {
        page: this.data.pagination.page,
        limit: this.data.pagination.limit
      };
      if (this.data.filters.search) params.search = this.data.filters.search;
      if (this.data.filters.estado) params.estado = this.data.filters.estado;

      const response = await trackingService.getVehiculos(params);
      this.data.vehiculos = response.data || [];
      this.data.pagination = response.pagination || this.data.pagination;
      this.data.loading = false;

      this.renderVehicleList();
      this.updateMarkers();
      this.actualizarHoraUpdate();
    } catch (error) {
      console.error('Error cargando vehículos:', error);
      const list = document.getElementById('vehicleList');
      if (list) {
        list.innerHTML = '<div style="padding: 1rem; text-align: center; color: var(--danger);">Error al cargar veh&iacute;culos</div>';
      }
    }
  },

  /** «Agregar / editar»: la tabla de ligas GPS ↔ unidad (D10). */
  async cargarLigas() {
    const body = document.getElementById('trkLigaBody');
    if (!body) return;
    try {
      const [rg, ru] = await Promise.all([
        unidadesSvc.gpsVehiculos(),
        permisos.canView('tractores') ? unidadesSvc.listar({ limit: 200 }) : Promise.resolve(null)
      ]);
      const vehiculos = rg?.data?.vehiculos || [];
      const lista = document.getElementById('trkUnidadesLista');
      if (lista && ru?.data?.unidades) {
        lista.innerHTML = ru.data.unidades.map((u) => `<option value="${escT(u.num_serie)}">${escT(u.modelo)} · ${escT(u.estado)}</option>`).join('');
      }
      body.innerHTML = vehiculos.map((g) => `
        <tr>
          <td><strong>${escT(g.nombre_activo)}</strong><div class="text-muted" style="font-size:.75rem;">placa ${escT(g.placa)}${g.activo ? '' : ' · inactivo'}</div></td>
          <td><code>${escT(g.num_serie)}</code></td>
          <td>${g.unidad_num_serie
            ? `<a href="/unidades?ver=${encodeURIComponent(g.unidad_num_serie)}" data-link>${escT(g.unidad_num_serie)}</a><div class="text-muted" style="font-size:.75rem;">${escT(g.unidad_modelo || '')} · ${escT(g.unidad_estado || '')}${g.unidad_alta_origen === 'gps' ? ' · entró del GPS' : ''}</div>`
            : '<span class="badge badge-warning">Sin unidad</span>'}</td>
          <td><input type="text" class="form-control" id="trkLiga_${g.id}" list="trkUnidadesLista" maxlength="50" style="text-transform:uppercase;min-width:200px;"
                     value="${escT(g.unidad_num_serie || '')}" placeholder="Serie de la unidad" /></td>
          <td style="white-space:nowrap;"><button type="button" class="btn btn-sm btn-primary" onclick="trackingView.guardarLiga(${g.id})">Guardar</button></td>
        </tr>`).join('') || '<tr><td colspan="5" class="text-center text-muted">No hay vehículos GPS</td></tr>';
    } catch (err) {
      body.innerHTML = `<tr><td colspan="5" class="text-center" style="color:var(--danger);">${escT(err.message || 'No se pudieron cargar los vehículos')}</td></tr>`;
    }
  },

  async guardarLiga(vehiculoId) {
    const input = document.getElementById(`trkLiga_${vehiculoId}`);
    const serie = (input?.value || '').trim().toUpperCase();
    try {
      const r = await unidadesSvc.ligarGps(vehiculoId, serie || null);
      window.toast?.success(r?.message || 'Liga guardada');
      await Promise.all([this.cargarLigas(), this.cargarVehiculos()]);
    } catch (err) {
      const det = Array.isArray(err.details) && err.details.length ? err.details.map((d) => d.message).join(' · ') : null;
      window.toast?.error(det || err.message || 'No se pudo guardar la liga');
    }
  },

  renderVehicleList() {
    const list = document.getElementById('vehicleList');
    if (!list) return;

    if (this.data.vehiculos.length === 0) {
      list.innerHTML = '<div style="padding: 2rem; text-align: center; color: var(--text-muted);">No se encontraron veh&iacute;culos</div>';
      return;
    }

    // El usuario distribuidor solo ve sus vehículos; el chip sería redundante
    const mostrarDistribuidor = !authService.hasRole('distribuidor');

    list.innerHTML = this.data.vehiculos.map(v => {
      const estadoMotor = trackingService.getEstadoMotorInfo(v.estado_motor);
      const estadoReporte = trackingService.getEstadoReporteInfo(v.fecha_hora);
      // Con filtro Total: punto = antiguedad. Con Encendidos/Apagados/Movimiento: punto sigue el filtro.
      const puntoFiltro = this._colorPuntoPorFiltro(estadoReporte);
      const isSelected = this.data.selectedVehicleId === v.id;
      const fechaHora = trackingService.formatFechaHora(v.fecha_hora);
      const chipDistribuidor = mostrarDistribuidor
        ? (v.distribuidor_nombre
            ? `<span class="vehicle-row-distribuidor" title="Distribuidor">&#x1F3E2; ${v.distribuidor_nombre}</span>`
            : '<span class="vehicle-row-distribuidor vehicle-row-distribuidor-none">Sin asignar</span>')
        : '';

      return `
        <div class="vehicle-row ${isSelected ? 'active' : ''}"
             id="vehicle-card-${v.id}"
             onclick="trackingView.selectVehicle(${v.id})">
          <div class="vehicle-row-activo">
            <div class="vehicle-row-name">
              <span class="vehicle-dot-reporte" style="background:${puntoFiltro.color}" title="${puntoFiltro.texto}"></span>
              <a href="/admin/tracking/${v.id}" data-link class="vehicle-serie-link" onclick="event.stopPropagation()">${trackingService.etiquetaVehiculo(v)}</a>
            </div>
            <span class="vehicle-row-placa">${v.placa}</span>
            <span class="${estadoMotor.claseCss}">${estadoMotor.texto}</span>
            ${chipDistribuidor}
            ${mostrarDistribuidor && !v.unidad_num_serie ? '<span class="vehicle-row-distribuidor vehicle-row-distribuidor-none" title="Este GPS no está ligado a ninguna unidad">Sin unidad</span>' : ''}
            ${v.direccion ? `<div class="vehicle-row-address">${v.direccion}</div>` : ''}
          </div>
          <div class="vehicle-row-reporte">
            <div class="vehicle-row-time">${fechaHora}</div>
            ${v.tiempo_detenido ? `<span class="vehicle-row-stopped">&#9201; ${v.tiempo_detenido}</span>` : ''}
            <span class="vehicle-row-temp">${v.temperatura_motor !== null ? `&#x1F321; ${v.temperatura_motor}&deg;C` : ''}${v.horometro != null ? ` &#9202; ${v.horometro}h` : (v.uso_clutch !== null ? ` &#9881; ${v.uso_clutch}h` : '')}</span>
          </div>
          <div class="vehicle-row-acciones" onclick="event.stopPropagation()">
            <button class="tracking-action-btn" title="Trazar ruta" onclick="trackingView.openDetailPanel(${v.id}, 'recorridos')">
              &#x1F5FA;
            </button>
            <button class="tracking-action-btn" title="Alertas" onclick="trackingView.openDetailPanel(${v.id}, 'alertas')">
              &#x26A0;
            </button>
            <button class="tracking-action-btn" title="Eventos" onclick="trackingView.openDetailPanel(${v.id}, 'eventos')">
              &#x1F4CB;
            </button>
          </div>
        </div>
      `;
    }).join('');
  },

  // --- Panel de detalle (slide-in) ---
  openDetailPanel(vehicleId, type) {
    const vehiculo = this.data.vehiculos.find(v => v.id === vehicleId);
    if (!vehiculo) return;

    this.data.detailVehicle = vehiculo;
    this.data.detailPanelType = type;
    // Reiniciar filtro de categoria al cambiar de vehiculo/pestaña
    this.data.eventosCategoriaFiltro = '';
    this.data.alertasCategoriaFiltro = '';

    const panel = document.getElementById('detailPanel');
    const title = document.getElementById('detailTitle');
    const body = document.getElementById('detailBody');
    if (!panel || !title || !body) return;

    const hoy = this.hoyLocal();
    const typeLabels = { recorridos: 'Recorridos', alertas: 'Alertas', eventos: 'Eventos' };
    const btnLabel = type === 'recorridos' ? 'Ver Recorridos' : 'Aplicar filtro';
    const fnName = type === 'recorridos' ? 'verRecorridos' : type === 'alertas' ? 'verAlertasPanel' : 'verEventos';

    title.textContent = `${typeLabels[type] || type} - ${trackingService.etiquetaVehiculo(vehiculo)}`;
    body.innerHTML = `
      <div class="detail-section">
        <div class="detail-form-row">
          <div class="form-group">
            <label class="form-label">Fecha inicial</label>
            <input type="date" class="form-control form-control-sm" id="detailFechaInicio" value="${hoy}">
          </div>
          <div class="form-group">
            <label class="form-label">Fecha final</label>
            <input type="date" class="form-control form-control-sm" id="detailFechaFin" value="${hoy}">
          </div>
        </div>
        <button class="btn btn-primary btn-sm" style="width: 100%; margin-top: 0.5rem;" onclick="trackingView.${fnName}()">${btnLabel}</button>
      </div>
      <div id="detailResult" style="padding: 1rem; text-align: center; color: var(--text-muted); font-size: 0.85rem;">
        Cargando...
      </div>
    `;

    // Abrir con animación
    panel.classList.add('open');
    this.data.detailPanelOpen = true;

    // Seleccionar vehículo en lista y mapa (sin abrir panel de info)
    this.highlightVehicle(vehicleId);

    // Auto-cargar datos del día actual
    if (type === 'recorridos') {
      this.verRecorridos();
    } else if (type === 'alertas') {
      this.verAlertasPanel();
    } else {
      this.verEventos();
    }
  },

  closeDetailPanel() {
    const panel = document.getElementById('detailPanel');
    if (panel) {
      panel.classList.remove('open');
    }
    this.data.detailPanelOpen = false;
    this.data.detailPanelType = null;
    this.data.detailVehicle = null;

    // Limpiar rutas dibujadas
    if (this.data.routePolyline) {
      this.data.routePolyline.setMap(null);
      this.data.routePolyline = null;
    }
    if (this.data.routeMarkers) {
      this.data.routeMarkers.forEach(m => m.setMap(null));
      this.data.routeMarkers = [];
    }
    this.clearTabRoutes();
  },

  async verRecorridos() {
    const vehiculo = this.data.detailVehicle;
    if (!vehiculo) return;

    const fechaInicio = document.getElementById('detailFechaInicio')?.value;
    const fechaFin = document.getElementById('detailFechaFin')?.value;
    if (!fechaInicio || !fechaFin) {
      window.toast?.warning('Seleccione rango de fechas');
      return;
    }

    const result = document.getElementById('detailResult');
    if (result) result.innerHTML = '<div style="padding: 1rem; text-align: center; color: var(--text-muted);">Cargando recorridos...</div>';

    try {
      const response = await trackingService.getRecorridos(vehiculo.id, fechaInicio, fechaFin);
      const recorridos = response.data.recorridos || [];

      if (recorridos.length === 0) {
        if (result) result.innerHTML = '<div style="padding: 1rem; text-align: center; color: var(--text-muted);">No se encontraron recorridos en este rango</div>';
        return;
      }

      // Dibujar polyline en el mapa
      this.drawRoute(recorridos);

      const r = response.data?.resumen || {};
      const s = r.sensores || {};
      // Fallback: calcular inicio/fin desde los puntos si el resumen no los trae
      const fechaInicio2 = r.fecha_inicio || recorridos[0]?.fecha_hora;
      const fechaFin2 = r.fecha_fin || recorridos[recorridos.length - 1]?.fecha_hora;

      if (result) {
        const v = (val, suffix) => (val !== undefined && val !== null) ? `${val}${suffix || ''}` : '-';
        const sensorBlock = (label, data, unit) => {
          if (!data) return '';
          return `
            <div class="rec-sensor-title">${label}</div>
            <div class="rec-detail-row">
              <span>Min: <strong>${data.min}</strong>${unit}</span>
              <span>Max: <strong>${data.max}</strong>${unit}</span>
              <span>Prom: <strong>${data.prom}</strong>${unit}</span>
            </div>`;
        };

        result.innerHTML = `
          <div class="rec-detail-panel">
            <div class="rec-detail-grid">
              <div class="rec-detail-item"><span class="rec-detail-label">Inicio:</span><span class="rec-detail-val">${trackingService.formatFechaHora(fechaInicio2)}</span></div>
              <div class="rec-detail-item"><span class="rec-detail-label">Final:</span><span class="rec-detail-val">${trackingService.formatFechaHora(fechaFin2)}</span></div>
            </div>
            ${r.distancia_odometro ? `<div class="rec-detail-grid"><div class="rec-detail-item"><span class="rec-detail-label">Distancia:</span><span class="rec-detail-val">${r.distancia_odometro} km</span></div><div class="rec-detail-item"><span class="rec-detail-label">Distancia recta:</span><span class="rec-detail-val">-</span></div></div>` : ''}
            <div class="rec-detail-grid">
              <div class="rec-detail-item"><span class="rec-detail-label">Vel. m&aacute;xima:</span><span class="rec-detail-val">${v(r.velocidad_maxima, ' km/h')}</span></div>
              <div class="rec-detail-item"><span class="rec-detail-label">Vel. promedio:</span><span class="rec-detail-val">${v(r.velocidad_promedio, ' km/h')}</span></div>
            </div>
            <div class="rec-detail-grid">
              <div class="rec-detail-item"><span class="rec-detail-label">Tiempo detenido:</span><span class="rec-detail-val">${v(r.tiempo_detenido)}</span></div>
              <div class="rec-detail-item"><span class="rec-detail-label">Tiempo movimiento:</span><span class="rec-detail-val">${v(r.tiempo_movimiento)}</span></div>
            </div>
            <div class="rec-detail-grid">
              <div class="rec-detail-item"><span class="rec-detail-label">Tiempo ralent&iacute;:</span><span class="rec-detail-val">${v(r.tiempo_ralenti)}</span></div>
              <div class="rec-detail-item"><span class="rec-detail-label">Tiempo total:</span><span class="rec-detail-val"><strong>${v(r.tiempo_total)}</strong></span></div>
            </div>
            <div class="rec-detail-grid">
              <div class="rec-detail-item"><span class="rec-detail-label">Horas uso motor:</span><span class="rec-detail-val">${v(r.tiempo_motor_encendido)}</span></div>
              <div class="rec-detail-item"><span class="rec-detail-label">Eventos:</span><span class="rec-detail-val">${v(r.eventos)}</span></div>
            </div>
            ${r.odometro_inicial ? `<div class="rec-detail-grid"><div class="rec-detail-item"><span class="rec-detail-label">Od&oacute;metro inicial:</span><span class="rec-detail-val">${r.odometro_inicial} km</span></div><div class="rec-detail-item"><span class="rec-detail-label">Od&oacute;metro final:</span><span class="rec-detail-val">${r.odometro_final} km</span></div></div>` : ''}
            ${sensorBlock('Bater&iacute;a GPS', s.bateria_gps, '%')}
            ${sensorBlock('Bater&iacute;a veh&iacute;culo', s.bateria_vehiculo, ' V')}
            ${sensorBlock('Se&ntilde;al', s.senal, '')}
          </div>
        `;
      }
    } catch (error) {
      console.error('Error cargando recorridos:', error);
      if (result) result.innerHTML = '<div style="padding: 1rem; color: var(--danger);">Error al cargar recorridos</div>';
    }
  },

  drawRoute(recorridos) {
    if (!this.data.map) return;

    // Limpiar ruta anterior
    if (this.data.routePolyline) {
      this.data.routePolyline.setMap(null);
      this.data.routePolyline = null;
    }
    if (this.data.routeMarkers) {
      this.data.routeMarkers.forEach(m => m.setMap(null));
      this.data.routeMarkers = [];
    }

    const path = recorridos
      .filter(r => r.latitude && r.longitude)
      .map(r => ({ lat: r.latitude, lng: r.longitude }));

    if (path.length === 0) return;

    // Polyline
    this.data.routePolyline = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#1a73e8',
      strokeOpacity: 0.8,
      strokeWeight: 3,
      map: this.data.map
    });

    // Marcadores inicio/fin
    this.data.routeMarkers = [];
    this.data.routeMarkers.push(new google.maps.Marker({
      position: path[0],
      map: this.data.map,
      label: { text: 'A', color: '#fff', fontSize: '11px', fontWeight: 'bold' },
      icon: { path: google.maps.SymbolPath.CIRCLE, fillColor: '#28a745', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2, scale: 10 },
      title: 'Inicio'
    }));
    this.data.routeMarkers.push(new google.maps.Marker({
      position: path[path.length - 1],
      map: this.data.map,
      label: { text: 'B', color: '#fff', fontSize: '11px', fontWeight: 'bold' },
      icon: { path: google.maps.SymbolPath.CIRCLE, fillColor: '#dc3545', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2, scale: 10 },
      title: 'Fin'
    }));

    // Ajustar mapa
    const bounds = new google.maps.LatLngBounds();
    path.forEach(p => bounds.extend(p));
    this.data.map.fitBounds(bounds, { padding: 50 });
  },

  async verEventos() {
    const vehiculo = this.data.detailVehicle;
    if (!vehiculo) return;

    const fechaInicio = document.getElementById('detailFechaInicio')?.value;
    const fechaFin = document.getElementById('detailFechaFin')?.value;
    if (!fechaInicio || !fechaFin) {
      window.toast?.warning('Seleccione rango de fechas');
      return;
    }

    const result = document.getElementById('detailResult');
    if (result) result.innerHTML = '<div style="padding: 1rem; text-align: center; color: var(--text-muted);">Cargando eventos...</div>';

    try {
      const response = await trackingService.getEventos(vehiculo.id, fechaInicio, fechaFin);
      const eventos = response.data.eventos || [];

      if (eventos.length === 0) {
        if (result) result.innerHTML = '<div style="padding: 1rem; text-align: center; color: var(--text-muted);">No se encontraron eventos en este rango</div>';
        return;
      }

      // Guardar eventos para acciones (más reciente primero)
      eventos.reverse();
      this.data.eventosDataAll = eventos;
      this.data.eventosData = eventos;

      this.renderEventosTable(eventos);
    } catch (error) {
      console.error('Error cargando eventos:', error);
      if (result) result.innerHTML = '<div style="padding: 1rem; color: var(--danger);">Error al cargar eventos</div>';
    }
  },

  /**
   * Dibuja la tabla de eventos + dropdown de filtro por categoría (categorías únicas del resultado)
   */
  renderEventosTable(eventos) {
    const result = document.getElementById('detailResult');
    if (!result) return;

    if (eventos.length === 0) {
      result.innerHTML = '<div style="padding: 1rem; text-align: center; color: var(--text-muted);">No se encontraron eventos en este rango</div>';
      return;
    }

    const categorias = [...new Set((this.data.eventosDataAll || eventos).map(e => e.categoria).filter(Boolean))].sort();
    const filtroActual = this.data.eventosCategoriaFiltro || '';

    result.innerHTML = `
      <div style="padding: 0 0.5rem;">
        ${categorias.length > 0 ? `
          <div style="margin-bottom: 0.5rem;">
            <select id="eventosCategoriaFilter" class="form-select" style="font-size: 0.8rem; width: auto;" onchange="trackingView.filtrarEventosPorCategoria(this.value)">
              <option value="">Todas las categor&iacute;as</option>
              ${categorias.map(c => `<option value="${c}" ${filtroActual === c ? 'selected' : ''}>${c}</option>`).join('')}
            </select>
          </div>
        ` : ''}
        <table class="tracking-events-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Categor&iacute;a</th>
              <th>Evento</th>
              <th style="width: 60px; text-align: center;">Acci&oacute;n</th>
            </tr>
          </thead>
          <tbody>
            ${eventos.map((e, i) => `
              <tr>
                <td>${trackingService.formatFechaHora(e.fecha_hora)}</td>
                <td>${e.categoria || '-'}</td>
                <td>${e.evento}</td>
                <td style="text-align: center; white-space: nowrap;">
                  ${e.latitude ? `<button class="tracking-action-btn" title="Ubicar" onclick="trackingView.ubicarEvento(${i})">&#x1F4CD;</button>` : ''}
                  <button class="tracking-action-btn" title="Ver detalle" onclick="trackingView.verDetalleEvento(${i})">&#x1F441;</button>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  },

  /**
   * Filtra la tabla de eventos ya cargada por categoría (sin volver a llamar al backend)
   */
  filtrarEventosPorCategoria(categoria) {
    this.data.eventosCategoriaFiltro = categoria;
    const todos = this.data.eventosDataAll || [];
    const filtrados = categoria ? todos.filter(e => e.categoria === categoria) : todos;
    this.data.eventosData = filtrados;
    this.renderEventosTable(filtrados);
  },

  async verAlertasPanel() {
    const vehiculo = this.data.detailVehicle;
    if (!vehiculo) return;

    const fechaInicio = document.getElementById('detailFechaInicio')?.value;
    const fechaFin = document.getElementById('detailFechaFin')?.value;
    if (!fechaInicio || !fechaFin) {
      window.toast?.warning('Seleccione rango de fechas');
      return;
    }

    const result = document.getElementById('detailResult');
    if (result) result.innerHTML = '<div style="padding: 1rem; text-align: center; color: var(--text-muted);">Cargando alertas...</div>';

    try {
      const response = await trackingService.getAlertas(vehiculo.id, fechaInicio, fechaFin);
      const alertas = response.data.alertas || [];

      if (alertas.length === 0) {
        if (result) result.innerHTML = '<div style="padding: 1rem; text-align: center; color: var(--text-muted);">No se encontraron alertas en este rango</div>';
        return;
      }

      // Más reciente primero
      alertas.reverse();
      this.data.alertasDataAll = alertas;
      this.data.alertasData = alertas;

      this.renderAlertasTable(alertas);
    } catch (error) {
      console.error('Error cargando alertas:', error);
      if (result) result.innerHTML = '<div style="padding: 1rem; color: var(--danger);">Error al cargar alertas</div>';
    }
  },

  /**
   * Dibuja la tabla de alertas + dropdown de filtro por categoría (categorías únicas del resultado)
   */
  renderAlertasTable(alertas) {
    const result = document.getElementById('detailResult');
    if (!result) return;

    if (alertas.length === 0) {
      result.innerHTML = '<div style="padding: 1rem; text-align: center; color: var(--text-muted);">No se encontraron alertas en este rango</div>';
      return;
    }

    const categorias = [...new Set((this.data.alertasDataAll || alertas).map(a => a.categoria).filter(Boolean))].sort();
    const filtroActual = this.data.alertasCategoriaFiltro || '';

    result.innerHTML = `
      <div style="padding: 0 0.5rem;">
        ${categorias.length > 0 ? `
          <div style="margin-bottom: 0.5rem;">
            <select id="alertasCategoriaFilter" class="form-select" style="font-size: 0.8rem; width: auto;" onchange="trackingView.filtrarAlertasPorCategoria(this.value)">
              <option value="">Todas las categor&iacute;as</option>
              ${categorias.map(c => `<option value="${c}" ${filtroActual === c ? 'selected' : ''}>${c}</option>`).join('')}
            </select>
          </div>
        ` : ''}
        <table class="tracking-events-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Categor&iacute;a</th>
              <th>Alerta</th>
              <th style="width: 60px; text-align: center;">Acci&oacute;n</th>
            </tr>
          </thead>
          <tbody>
            ${alertas.map((a, i) => `
              <tr>
                <td>${trackingService.formatFechaHora(a.fecha_hora)}</td>
                <td>${a.categoria || '-'}</td>
                <td>${a.evento}</td>
                <td style="text-align: center; white-space: nowrap;">
                  ${a.latitude ? `<button class="tracking-action-btn" title="Ubicar" onclick="trackingView.ubicarAlerta(${i})">&#x1F4CD;</button>` : ''}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  },

  /**
   * Filtra la tabla de alertas ya cargada por categoría (sin volver a llamar al backend)
   */
  filtrarAlertasPorCategoria(categoria) {
    this.data.alertasCategoriaFiltro = categoria;
    const todos = this.data.alertasDataAll || [];
    const filtrados = categoria ? todos.filter(a => a.categoria === categoria) : todos;
    this.data.alertasData = filtrados;
    this.renderAlertasTable(filtrados);
  },

  ubicarAlerta(index) {
    const alerta = this.data.alertasData?.[index];
    if (!alerta || !alerta.latitude || !alerta.longitude || !this.data.map) return;
    const position = { lat: alerta.latitude, lng: alerta.longitude };
    this.data.map.panTo(position);
    this.data.map.setZoom(16);
    if (this.data.infoWindow) {
      this.data.infoWindow.setContent(`
        <div style="font-family:inherit; padding:4px; min-width:180px;">
          <div style="font-weight:bold; font-size:13px;">${trackingService.etiquetaVehiculo(this.data.detailVehicle)}</div>
          <div style="font-size:12px; color:var(--danger);"><strong>${alerta.evento}</strong></div>
          <div style="font-size:11px; color:#666;">${trackingService.formatFechaHora(alerta.fecha_hora)}</div>
          ${alerta.direccion ? `<div style="font-size:11px; color:#666;">${alerta.direccion}</div>` : ''}
        </div>
      `);
      this.data.infoWindow.setPosition(position);
      this.data.infoWindow.open(this.data.map);
    }
  },

  ubicarEvento(index) {
    const evento = this.data.eventosData?.[index];
    if (!evento || !evento.latitude || !evento.longitude || !this.data.map) return;

    const position = { lat: evento.latitude, lng: evento.longitude };
    this.data.map.panTo(position);
    this.data.map.setZoom(16);

    // Mostrar info window en la posición del evento
    if (this.data.infoWindow) {
      this.data.infoWindow.setContent(`
        <div style="font-family: inherit; padding: 4px; min-width: 180px;">
          <div style="font-weight: bold; font-size: 13px; margin-bottom: 4px;">${trackingService.etiquetaVehiculo(this.data.detailVehicle)}</div>
          <div style="font-size: 12px;"><strong>${evento.evento}</strong></div>
          <div style="font-size: 11px; color: #666;">${trackingService.formatFechaHora(evento.fecha_hora)}</div>
          ${evento.velocidad ? `<div style="font-size: 11px; color: #666;">${evento.velocidad} km/h</div>` : ''}
          ${evento.direccion ? `<div style="font-size: 11px; color: #666;">${evento.direccion}</div>` : ''}
        </div>
      `);
      this.data.infoWindow.setPosition(position);
      this.data.infoWindow.open(this.data.map);
    }
  },

  verDetalleEvento(index) {
    const evento = this.data.eventosData?.[index];
    if (!evento) return;

    const vehiculo = this.data.detailVehicle;
    const fechaHora = trackingService.formatFechaHora(evento.fecha_hora);

    // Mostrar modal con detalle del evento
    const modalHtml = `
      <div class="modal" id="eventoDetalleModal" style="display: flex;">
        <div class="modal-content" style="max-width: 550px;">
          <div class="modal-header">
            <h2 style="font-size: 1rem;">${trackingService.etiquetaVehiculo(vehiculo)} - ${evento.evento}</h2>
            <button class="modal-close" onclick="document.getElementById('eventoDetalleModal').remove()">&times;</button>
          </div>
          <div class="modal-body">
            <p style="font-size: 0.85rem; color: var(--text-muted); margin-bottom: 1rem;">
              ${trackingService.etiquetaVehiculo(vehiculo)}, ${vehiculo?.placa} gener&oacute; evento <strong>"${evento.evento}"</strong> el ${fechaHora}
            </p>
            <table class="tracking-info-table">
              <tr><td class="info-label">Placa:</td><td class="info-value">${vehiculo?.placa || ''}</td></tr>
              <tr><td class="info-label">Activo:</td><td class="info-value">${trackingService.etiquetaVehiculo(vehiculo)}</td></tr>
              <tr><td class="info-label">Dispositivo:</td><td class="info-value">${vehiculo?.imei || 'N/A'}</td></tr>
              <tr><td class="info-label">Evento:</td><td class="info-value"><strong>${evento.evento}</strong></td></tr>
              <tr><td class="info-label">Fecha y hora:</td><td class="info-value">${fechaHora}</td></tr>
              ${evento.latitude ? `<tr><td class="info-label">Posici&oacute;n:</td><td class="info-value">${evento.latitude}, ${evento.longitude}</td></tr>` : ''}
              ${evento.velocidad !== undefined ? `<tr><td class="info-label">Velocidad:</td><td class="info-value">${evento.velocidad} km/h</td></tr>` : ''}
              ${evento.direccion ? `<tr><td class="info-label">Domicilio:</td><td class="info-value">${evento.direccion}</td></tr>` : ''}
            </table>
          </div>
          <div class="modal-footer">
            ${evento.latitude ? `<button class="btn btn-secondary btn-sm" onclick="trackingView.ubicarEvento(${index}); document.getElementById('eventoDetalleModal').remove();">Ubicar en mapa</button>` : ''}
            <button class="btn btn-primary btn-sm" onclick="document.getElementById('eventoDetalleModal').remove()">Cerrar</button>
          </div>
        </div>
      </div>
    `;

    // Remover modal anterior si existe
    document.getElementById('eventoDetalleModal')?.remove();
    document.body.insertAdjacentHTML('beforeend', modalHtml);
  },

  // --- Mapa ---
  async initMap() {
    try {
      await loadGoogleMaps();

      const mapElement = document.getElementById('trackingMap');
      if (!mapElement) return;

      this.data.map = new google.maps.Map(mapElement, {
        center: { lat: 19.3, lng: -98.2 },
        zoom: 8,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true
      });

      this.data.infoWindow = new google.maps.InfoWindow();

      this.updateMarkers();
    } catch (error) {
      console.error('Error inicializando mapa:', error);
      const mapElement = document.getElementById('trackingMap');
      if (mapElement) {
        mapElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:var(--text-muted);">Error al cargar el mapa</div>';
      }
    }
  },

  updateMarkers() {
    if (!this.data.map) return;

    // Si el usuario eligio un activo sin posicion/reporte valido, no dejar pins de OTROS equipos
    // (si no, parece que el mapa sigue mostrando la ubicacion del GPS anterior).
    const selId = this.data.selectedVehicleId;
    const sel = selId != null
      ? this.data.vehiculos.find((x) => x.id === selId || String(x.id) === String(selId))
      : null;
    if (sel && !vehiculoMostrarPinEnMapa(sel)) {
      Object.keys(this.data.markers).forEach((id) => {
        const m = this.data.markers[id];
        if (m) {
          m.setMap(null);
          delete this.data.markers[id];
        }
      });
      try {
        this.data.infoWindow?.close();
      } catch (e) {
        /* ignore */
      }
      this.data.map.setCenter({ lat: 19.3, lng: -98.2 });
      this.data.map.setZoom(8);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    const currentIds = new Set();
    let hasValidPositions = false;

    this.data.vehiculos.forEach(v => {
      if (!vehiculoMostrarPinEnMapa(v)) return;

      const vid = String(v.id);
      currentIds.add(vid);
      hasValidPositions = true;
      const la = parseFloat(String(v.latitude).replace(',', '.'));
      const lo = parseFloat(String(v.longitude).replace(',', '.'));
      const position = { lat: la, lng: lo };
      bounds.extend(position);

      if (this.data.markers[vid]) {
        this.data.markers[vid].setPosition(position);
        this.data.markers[vid].setIcon(this.getMarkerIcon(v));
      } else {
        const marker = new google.maps.Marker({
          position,
          map: this.data.map,
          title: `${trackingService.etiquetaVehiculo(v)} (${v.placa})`,
          icon: this.getMarkerIcon(v)
        });

        marker.addListener('click', () => {
          this.showInfoWindow(v, marker);
          this.highlightVehicle(v.id, false);
        });

        this.data.markers[vid] = marker;
      }
    });

    Object.keys(this.data.markers).forEach(id => {
      if (!currentIds.has(String(id))) {
        this.data.markers[id].setMap(null);
        delete this.data.markers[id];
      }
    });

    // Solo ajustar bounds en la carga inicial, no en refresh
    if (!this.data.mapInitialized && hasValidPositions) {
      if (Object.keys(this.data.markers).length > 1) {
        this.data.map.fitBounds(bounds, { padding: 50 });
      } else {
        const firstVehicle = this.data.vehiculos.find((v) => vehiculoMostrarPinEnMapa(v));
        if (firstVehicle) {
          const la = parseFloat(String(firstVehicle.latitude).replace(',', '.'));
          const lo = parseFloat(String(firstVehicle.longitude).replace(',', '.'));
          this.data.map.setCenter({ lat: la, lng: lo });
          this.data.map.setZoom(14);
        }
      }
      this.data.mapInitialized = true;
    }
  },

  /**
   * Color del punto en lista segun filtro activo.
   * Total = antiguedad; Encendidos/Apagados/Movimiento = color del filtro.
   */
  _colorPuntoPorFiltro(estadoReporte) {
    const filtro = this.data.activeStatFilter;
    if (filtro === 'encendido') {
      return { color: '#2563eb', texto: 'Filtro: Encendidos' };
    }
    if (filtro === 'apagado') {
      return { color: '#6b7280', texto: 'Filtro: Apagados' };
    }
    if (filtro === 'movimiento') {
      return { color: '#3b82f6', texto: 'Filtro: En movimiento' };
    }
    return {
      color: estadoReporte?.color || '#9ca3af',
      texto: estadoReporte?.texto || 'Sin datos'
    };
  },

  getMarkerIcon(vehiculo) {
    // Prioridad: si hay filtro de chip (encendido/apagado/movimiento), ese color gana.
    // Con filtro Total: fill = antiguedad, anillo = motor.
    const estadoReporte = trackingService.getEstadoReporteInfo(vehiculo.fecha_hora);
    const estadoMotor = trackingService.getEstadoMotorInfo(vehiculo.estado_motor);
    const filtro = this.data.activeStatFilter;
    const puntoFiltro = this._colorPuntoPorFiltro(estadoReporte);

    let fillColor = puntoFiltro.color;
    let strokeColor = '#fff';
    let strokeWeight = 2;

    if (!filtro) {
      // Total: antiguedad + anillo por motor
      if (vehiculo.estado_motor === 'encendido') {
        strokeColor = '#2563eb';
        strokeWeight = 3;
      } else if (vehiculo.estado_motor === 'trampa_ignicion') {
        strokeColor = estadoMotor.color;
        strokeWeight = 3;
      }
    }

    return {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor,
      fillOpacity: 1,
      strokeColor,
      strokeWeight,
      scale: 8
    };
  },

  showInfoWindow(vehiculo, marker) {
    if (!this.data.infoWindow) return;

    const estadoMotor = trackingService.getEstadoMotorInfo(vehiculo.estado_motor);
    const estadoReporte = trackingService.getEstadoReporteInfo(vehiculo.fecha_hora);
    const fechaHora = trackingService.formatFechaHora(vehiculo.fecha_hora);
    // Badge inline de antiguedad (colores del plan)
    const badgeReporte = `<span style="font-size:11px;font-weight:600;padding:1px 6px;border-radius:10px;color:#fff;background:${estadoReporte.color};">${estadoReporte.texto}</span>`;

    const content = `
      <div style="font-family: inherit; min-width: 220px; padding: 4px;">
        <div style="font-weight: bold; font-size: 14px; margin-bottom: 6px;">${trackingService.etiquetaVehiculo(vehiculo)}</div>
        <div style="font-size: 12px; color: #666; margin-bottom: 8px;">Placa: ${vehiculo.placa} | IMEI: ${vehiculo.imei || 'N/A'}</div>
        <table style="font-size: 12px; width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 2px 8px 2px 0; color: #888;">Reporte:</td><td style="padding: 2px 0;">${badgeReporte}</td></tr>
          <tr><td style="padding: 2px 8px 2px 0; color: #888;">Motor:</td><td style="padding: 2px 0;"><span class="${estadoMotor.claseCss}">${estadoMotor.texto}</span></td></tr>
          <tr><td style="padding: 2px 8px 2px 0; color: #888;">Velocidad:</td><td style="padding: 2px 0;">${vehiculo.velocidad} km/h</td></tr>
          ${vehiculo.temperatura_motor !== null ? `<tr><td style="padding: 2px 8px 2px 0; color: #888;">Temp. Motor:</td><td style="padding: 2px 0;">${vehiculo.temperatura_motor}&deg;C</td></tr>` : ''}
          ${vehiculo.bateria_vehiculo ? `<tr><td style="padding: 2px 8px 2px 0; color: #888;">Bater&iacute;a:</td><td style="padding: 2px 0;">${vehiculo.bateria_vehiculo} V</td></tr>` : ''}
          <tr><td style="padding: 2px 8px 2px 0; color: #888;">&Uacute;ltimo reporte:</td><td style="padding: 2px 0;">${fechaHora}</td></tr>
          ${vehiculo.direccion ? `<tr><td style="padding: 2px 8px 2px 0; color: #888;">Ubicaci&oacute;n:</td><td style="padding: 2px 0;">${vehiculo.direccion}</td></tr>` : ''}
        </table>
        <div style="margin-top: 8px; text-align: right;">
          <a href="https://www.google.com/maps?q=${vehiculo.latitude},${vehiculo.longitude}" target="_blank" style="font-size: 11px; color: #1a73e8;">Ver en Google Maps &#8599;</a>
        </div>
      </div>
    `;

    this.data.infoWindow.setContent(content);
    this.data.infoWindow.open(this.data.map, marker);
  },

  highlightVehicle(vehicleId, centerMap = true) {
    if (this.data.selectedVehicleId) {
      const prevCard = document.getElementById(`vehicle-card-${this.data.selectedVehicleId}`);
      if (prevCard) prevCard.classList.remove('active');
    }

    this.data.selectedVehicleId = vehicleId;

    const card = document.getElementById(`vehicle-card-${vehicleId}`);
    if (card) {
      card.classList.add('active');
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    const mid = String(vehicleId);
    if (centerMap && this.data.markers[mid] && this.data.map) {
      const marker = this.data.markers[mid];
      this.data.map.panTo(marker.getPosition());
      this.data.map.setZoom(15);
    }
  },

  selectVehicle(vehicleId, centerMap = true) {
    const v = this.data.vehiculos.find((x) => x.id === vehicleId || String(x.id) === String(vehicleId));
    if (v && !vehiculoMostrarPinEnMapa(v)) {
      try {
        this.data.infoWindow?.close();
      } catch (e) {
        /* ignore */
      }
    }
    this.highlightVehicle(vehicleId, centerMap);
    this.openDeviceInfoPanel(vehicleId);
    // Sincroniza pins al elegir fila: quita marcador obsoleto si el activo paso a sin datos sin esperar al refresh
    if (this.data.map) this.updateMarkers();
  },

  openDeviceInfoPanel(vehicleId) {
    const vehiculo = this.data.vehiculos.find(
      (v) => v.id === vehicleId || String(v.id) === String(vehicleId)
    );
    if (!vehiculo) return;

    const panel = document.getElementById('detailPanel');
    const title = document.getElementById('detailTitle');
    const body = document.getElementById('detailBody');
    if (!panel || !title || !body) return;

    this.data.detailVehicle = vehiculo;
    this.data.detailPanelType = 'info';

    const d = (v, s) => (v !== null && v !== undefined) ? `${v}${s || ''}` : 'N/A';
    const estadoMotor = trackingService.getEstadoMotorInfo(vehiculo.estado_motor);
    const fechaHora = trackingService.formatFechaHora(vehiculo.fecha_hora);
    const estadoTexto = `<span class="${estadoMotor.claseCss}">${estadoMotor.texto}</span>`;
    // Aviso destacado solo para la trampa de ignición (anomalía que el usuario debe notar de inmediato)
    const motorSwitchAlerta = vehiculo.estado_motor === 'trampa_ignicion'
      ? '<span style="color:var(--warning); font-weight:600;">&#9888; Unidad trabajando con switch apagado</span>' : '';

    title.textContent = trackingService.etiquetaVehiculo(vehiculo);
    body.innerHTML = `
      <div class="rec-detail-panel">
        <div class="rec-sensor-title">Informaci&oacute;n del activo</div>
        <div class="rec-detail-grid">
          <div class="rec-detail-item"><span class="rec-detail-label">Placa:</span><span class="rec-detail-val">${vehiculo.placa}</span></div>
          <div class="rec-detail-item"><span class="rec-detail-label">Serie:</span><span class="rec-detail-val">${trackingService.etiquetaVehiculo(vehiculo)}</span></div>
        </div>
        ${!authService.hasRole('distribuidor') ? `<div class="rec-detail-grid">
          <div class="rec-detail-item" style="grid-column:1/-1;"><span class="rec-detail-label">Distribuidor:</span><span class="rec-detail-val">${vehiculo.distribuidor_nombre || '<span style="color:var(--gray-500); font-style:italic;">Sin asignar</span>'}</span></div>
        </div>` : ''}
        <div class="rec-detail-grid">
          <div class="rec-detail-item"><span class="rec-detail-label">Dispositivo:</span><span class="rec-detail-val" style="font-size:0.72rem;">${vehiculo.imei || 'N/A'}</span></div>
          <div class="rec-detail-item"><span class="rec-detail-label">Ignici&oacute;n:</span><span class="rec-detail-val">${estadoTexto}</span></div>
        </div>
        <div class="rec-detail-grid">
          <div class="rec-detail-item" style="grid-column:1/-1;"><span class="rec-detail-label">Posici&oacute;n:</span><span class="rec-detail-val">${vehiculo.latitude ? `${vehiculo.latitude}, ${vehiculo.longitude}` : 'Sin datos'}</span></div>
        </div>
        <div class="rec-detail-grid">
          <div class="rec-detail-item"><span class="rec-detail-label">Link Google Maps:</span>${vehiculo.latitude ? `<a href="https://www.google.com/maps?q=${vehiculo.latitude},${vehiculo.longitude}" target="_blank" style="font-size:0.78rem; color:var(--primary);">Ver Mapa</a>` : '<span class="rec-detail-val">N/A</span>'}</div>
          <div class="rec-detail-item"><span class="rec-detail-label">&Uacute;ltimo reporte:</span><span class="rec-detail-val">${fechaHora}</span></div>
        </div>
        <div class="rec-detail-grid">
          <div class="rec-detail-item"><span class="rec-detail-label">Tiempo detenido:</span><span class="rec-detail-val">${vehiculo.tiempo_detenido || 'N/A'}</span></div>
          <div class="rec-detail-item"><span class="rec-detail-label">Hor&oacute;metro:</span><span class="rec-detail-val">${vehiculo.horometro != null ? `<span class="badge badge-warning" style="font-size:0.72rem;">${vehiculo.horometro} Hrs.</span>` : 'N/A'}</span></div>
        </div>
        <div class="rec-detail-grid">
          <div class="rec-detail-item"><span class="rec-detail-label">Od&oacute;metro:</span><span class="rec-detail-val">${vehiculo.odometro ? `<span class="badge badge-info" style="font-size:0.72rem;">${(parseFloat(vehiculo.odometro) / 1000).toFixed(3)} km</span>` : 'N/A'}</span></div>
        </div>
        ${vehiculo.direccion ? `<div class="rec-detail-grid"><div class="rec-detail-item" style="grid-column:1/-1;"><span class="rec-detail-label">Domicilio:</span><span class="rec-detail-val">${vehiculo.direccion}</span></div></div>` : ''}

        <div class="rec-sensor-title">Par&aacute;metros principales</div>
        <div class="rec-detail-grid">
          <div class="rec-detail-item"><span class="rec-detail-label">Pie en descanso:</span><span class="rec-detail-val">${d(vehiculo.pie_en_descanso, ' s')}</span></div>
          <div class="rec-detail-item"><span class="rec-detail-label">Uso de clutch:</span><span class="rec-detail-val">${d(vehiculo.uso_clutch, ' h')}</span></div>
        </div>
        <div class="rec-detail-grid">
          <div class="rec-detail-item"><span class="rec-detail-label">Temperatura motor:</span><span class="rec-detail-val">${d(vehiculo.temperatura_motor, ' &deg;C')}</span></div>
          <div class="rec-detail-item"><span class="rec-detail-label">Presi&oacute;n aceite:</span><span class="rec-detail-val">${d(vehiculo.presion_aceite)}</span></div>
        </div>
        ${motorSwitchAlerta ? `<div style="padding:0.3rem 0.2rem;">${motorSwitchAlerta}</div>` : ''}

        <div class="rec-sensor-title">Informaci&oacute;n sensores</div>
        <div class="rec-detail-grid">
          <div class="rec-detail-item"><span class="rec-detail-label">Velocidad:</span><span class="rec-detail-val">${vehiculo.velocidad} km/h</span></div>
          <div class="rec-detail-item"><span class="rec-detail-label">Bater&iacute;a dispositivo:</span><span class="rec-detail-val">${d(vehiculo.bateria_gps, ' %')}</span></div>
        </div>
        <div class="rec-detail-grid">
          <div class="rec-detail-item"><span class="rec-detail-label">Bater&iacute;a activo:</span><span class="rec-detail-val">${d(vehiculo.bateria_vehiculo, ' V')}</span></div>
          <div class="rec-detail-item"><span class="rec-detail-label">Hora:</span><span class="rec-detail-val">${fechaHora}</span></div>
        </div>

        <div style="padding:0.5rem 0.2rem; display:flex; flex-direction:column; gap:0.3rem;">
          <button class="btn btn-primary btn-sm" onclick="trackingView.openDetalleAvanzadoModal(${vehiculo.id})">Detalle avanzado</button>
          <button class="btn btn-sm" style="background:var(--info); color:#fff; border:none;" onclick="trackingView.openDetalleGrafico(${vehiculo.id})">Detalle gr&aacute;fico</button>
        </div>
      </div>
    `;

    panel.classList.add('open');
    this.data.detailPanelOpen = true;
  },

  async openDetalleAvanzadoModal(vehicleId) {
    try {
      const response = await trackingService.getVehiculoDetalle(vehicleId);
      const data = response.data;
      const v = data.vehiculo;
      const p = data.posicion;
      const s = data.sensores;
      const td = data.tiempo_detenido;

      const d = (val, suffix) => (val !== null && val !== undefined) ? `${val}${suffix || ''}` : 'N/A';
      const estadoMotor = trackingService.getEstadoMotorInfo(s?.estado_motor);

      const modalHtml = `
        <div class="modal" id="detalleAvanzadoModal" style="display:flex;">
          <div class="modal-content" style="max-width:650px; max-height:90vh; overflow-y:auto;">
            <div class="modal-header">
              <h2 style="font-size:1rem;">${trackingService.etiquetaVehiculo(v)}</h2>
              <button class="modal-close" onclick="document.getElementById('detalleAvanzadoModal').remove()">&times;</button>
            </div>
            <div class="modal-body" style="padding:0;">
              <div class="rec-detail-panel">
                <div class="rec-sensor-title">Informaci&oacute;n del activo</div>
                <div class="rec-detail-grid">
                  <div class="rec-detail-item"><span class="rec-detail-label">Placa:</span><span class="rec-detail-val">${v.placa}</span></div>
                  <div class="rec-detail-item"><span class="rec-detail-label">Serie:</span><span class="rec-detail-val">${trackingService.etiquetaVehiculo(v)}</span></div>
                </div>
                ${!authService.hasRole('distribuidor') ? `<div class="rec-detail-grid">
                  <div class="rec-detail-item" style="grid-column:1/-1;"><span class="rec-detail-label">Distribuidor:</span><span class="rec-detail-val">${v.distribuidor_nombre || '<span style="color:var(--gray-500); font-style:italic;">Sin asignar</span>'}</span></div>
                </div>` : ''}
                <div class="rec-detail-grid">
                  <div class="rec-detail-item"><span class="rec-detail-label">Dispositivo:</span><span class="rec-detail-val" style="font-size:0.72rem;">${v.imei || 'N/A'}</span></div>
                  <div class="rec-detail-item"><span class="rec-detail-label">Ignici&oacute;n:</span><span class="rec-detail-val"><span class="${estadoMotor.claseCss}">${estadoMotor.texto}</span></span></div>
                </div>
                <div class="rec-detail-grid">
                  <div class="rec-detail-item"><span class="rec-detail-label">Posici&oacute;n:</span><span class="rec-detail-val">${p?.latitude ? `${p.latitude}, ${p.longitude}` : 'N/A'}</span></div>
                  <div class="rec-detail-item"><span class="rec-detail-label">Link Google Maps:</span>${p?.latitude ? `<a href="https://www.google.com/maps?q=${p.latitude},${p.longitude}" target="_blank" style="font-size:0.78rem; color:var(--primary);">Ver Mapa</a>` : '<span class="rec-detail-val">N/A</span>'}</div>
                </div>
                <div class="rec-detail-grid">
                  <div class="rec-detail-item"><span class="rec-detail-label">&Uacute;ltimo reporte:</span><span class="rec-detail-val">${trackingService.formatFechaHora(p?.fecha_hora)}</span></div>
                  <div class="rec-detail-item"><span class="rec-detail-label">&Uacute;ltima conexi&oacute;n:</span><span class="rec-detail-val">${trackingService.formatFechaHora(p?.recibido_en)}</span></div>
                </div>
                <div class="rec-detail-grid">
                  <div class="rec-detail-item"><span class="rec-detail-label">Tiempo detenido:</span><span class="rec-detail-val">${td || 'N/A'}</span></div>
                </div>
                ${p?.direccion ? `<div class="rec-detail-grid"><div class="rec-detail-item" style="grid-column:1/-1;"><span class="rec-detail-label">Domicilio:</span><span class="rec-detail-val">${p.direccion}</span></div></div>` : ''}
                <div class="rec-detail-grid">
                  <div class="rec-detail-item"><span class="rec-detail-label">Hor&oacute;metro:</span><span class="rec-detail-val">${s?.horometro ? `<span class="badge badge-warning">${s.horometro} Hrs.</span>` : 'N/A'}</span></div>
                  <div class="rec-detail-item"><span class="rec-detail-label">Od&oacute;metro Total:</span><span class="rec-detail-val">${s?.odometro_km ? `<span class="badge badge-info">${s.odometro_km} km</span>` : 'N/A'}</span></div>
                </div>

                <div class="rec-sensor-title">Par&aacute;metros principales</div>
                <div class="rec-detail-grid">
                  <div class="rec-detail-item"><span class="rec-detail-label">Pie en descanso:</span><span class="rec-detail-val">${d(s?.pie_en_descanso, ' s')}</span></div>
                  <div class="rec-detail-item"><span class="rec-detail-label">Uso de clutch:</span><span class="rec-detail-val">${d(s?.uso_clutch, ' h')}</span></div>
                </div>
                <div class="rec-detail-grid">
                  <div class="rec-detail-item"><span class="rec-detail-label">Motor encendido:</span><span class="rec-detail-val"><span class="${estadoMotor.claseCss}">${estadoMotor.texto}</span></span></div>
                  <div class="rec-detail-item"><span class="rec-detail-label">Temperatura motor:</span><span class="rec-detail-val">${d(s?.temperatura_motor, ' &deg;C')}</span></div>
                </div>
                <div class="rec-detail-grid">
                  <div class="rec-detail-item"><span class="rec-detail-label">Presi&oacute;n aceite:</span><span class="rec-detail-val">${d(s?.presion_aceite)}</span></div>
                  <div class="rec-detail-item"><span class="rec-detail-label">Hora:</span><span class="rec-detail-val">${d(s?.hora)}</span></div>
                </div>

                <div class="rec-sensor-title">Informaci&oacute;n sensores</div>
                <div class="rec-detail-grid">
                  <div class="rec-detail-item"><span class="rec-detail-label">Velocidad:</span><span class="rec-detail-val">${d(s?.velocidad, ' km/h')}</span></div>
                  <div class="rec-detail-item"><span class="rec-detail-label">Direcci&oacute;n:</span><span class="rec-detail-val">${d(s?.direccion)} ${s?.direccion_texto || ''}</span></div>
                </div>
                <div class="rec-detail-grid">
                  <div class="rec-detail-item"><span class="rec-detail-label">Se&ntilde;al:</span><span class="rec-detail-val">${d(s?.senal)}</span></div>
                  <div class="rec-detail-item"><span class="rec-detail-label">Sat&eacute;lites:</span><span class="rec-detail-val">${d(s?.satelites)}</span></div>
                </div>
                <div class="rec-detail-grid">
                  <div class="rec-detail-item"><span class="rec-detail-label">Bater&iacute;a dispositivo:</span><span class="rec-detail-val">${d(s?.bateria_gps, ' %')}</span></div>
                  <div class="rec-detail-item"><span class="rec-detail-label">Bater&iacute;a activo:</span><span class="rec-detail-val">${d(s?.bateria_vehiculo, ' V')}</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;

      document.getElementById('detalleAvanzadoModal')?.remove();
      document.body.insertAdjacentHTML('beforeend', modalHtml);
    } catch (error) {
      console.error('Error cargando detalle avanzado:', error);
      window.toast?.error('Error al cargar el detalle del veh\u00edculo');
    }
  },

  // ====================== DETALLE GRÁFICO ======================

  async loadChartJs() {
    if (window.Chart) return;
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.7/dist/chart.umd.min.js';
      script.onload = () => {
        // Cargar adaptador de fechas
        const adapter = document.createElement('script');
        adapter.src = 'https://cdn.jsdelivr.net/npm/chartjs-adapter-date-fns@3.0.0/dist/chartjs-adapter-date-fns.bundle.min.js';
        adapter.onload = resolve;
        adapter.onerror = reject;
        document.head.appendChild(adapter);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  },

  async openDetalleGrafico(vehicleId) {
    const vehiculoRef = this.data.vehiculos.find(x => x.id === vehicleId);
    const etiquetaTitulo = trackingService.etiquetaVehiculo(vehiculoRef) || 'Activo';

    // Calcular rango default: último mes
    const hoy = this.hoyLocal();
    const hace1Mes = (() => {
      const d = new Date();
      d.setMonth(d.getMonth() - 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    })();

    // Remover modal anterior si existe
    document.getElementById('graficoModal')?.remove();

    const modalHtml = `
      <div class="modal" id="graficoModal" style="display:flex;">
        <div class="modal-content" style="max-width:950px; width:95%; max-height:92vh; display:flex; flex-direction:column;">
          <div class="modal-header" style="flex-shrink:0;">
            <h2 style="font-size:1rem;">Detalle Gr\u00e1fico - ${etiquetaTitulo}</h2>
            <button class="modal-close" onclick="document.getElementById('graficoModal').remove()">&times;</button>
          </div>
          <div style="padding:0.75rem 1rem 0; flex-shrink:0; display:flex; gap:0.5rem; align-items:end; flex-wrap:wrap;">
            <div>
              <label class="form-label" style="font-size:0.75rem; margin-bottom:0.15rem;">Fecha inicio</label>
              <input type="date" class="form-control" id="graficoFechaInicio" value="${hace1Mes}" style="font-size:0.82rem;">
            </div>
            <div>
              <label class="form-label" style="font-size:0.75rem; margin-bottom:0.15rem;">Fecha fin</label>
              <input type="date" class="form-control" id="graficoFechaFin" value="${hoy}" style="font-size:0.82rem;">
            </div>
            <button class="btn btn-primary btn-sm" onclick="trackingView.cargarGraficos(${vehicleId})">Actualizar</button>
            <span id="graficoStatus" style="font-size:0.75rem; color:var(--text-muted);"></span>
          </div>
          <div class="modal-body" id="graficoBody" style="flex:1; overflow-y:auto; padding:0.75rem 1rem;">
            <div style="text-align:center; padding:2rem; color:var(--text-muted);">Cargando gr\u00e1ficos...</div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);

    // Cargar Chart.js y datos
    try {
      await this.loadChartJs();
      await this.cargarGraficos(vehicleId);
    } catch (error) {
      console.error('Error cargando gráficos:', error);
      const body = document.getElementById('graficoBody');
      if (body) body.innerHTML = '<div style="padding:2rem; text-align:center; color:var(--danger);">Error al cargar los gr\u00e1ficos</div>';
    }
  },

  async cargarGraficos(vehicleId) {
    const fechaInicio = document.getElementById('graficoFechaInicio')?.value;
    const fechaFin = document.getElementById('graficoFechaFin')?.value;
    const body = document.getElementById('graficoBody');
    const status = document.getElementById('graficoStatus');

    if (!fechaInicio || !fechaFin) {
      window.toast?.warning('Seleccione un rango de fechas');
      return;
    }

    if (body) body.innerHTML = '<div style="text-align:center; padding:2rem; color:var(--text-muted);">Cargando datos...</div>';
    if (status) status.textContent = 'Consultando...';

    try {
      const response = await trackingService.getSensoresHistorico(vehicleId, fechaInicio, fechaFin);
      const data = response.data;
      const series = data.series;

      if (status) status.textContent = `${data.total} registros (${data.muestreados} puntos graficados)`;

      // Crear contenedores de canvas para cada grupo de sensor
      body.innerHTML = `
        <div class="grafico-section" id="seccion-temperatura"></div>
        <div class="grafico-section" id="seccion-horometro"></div>
        <div class="grafico-section" id="seccion-odometro"></div>
        <div class="grafico-section" id="seccion-clutch"></div>
        <div class="grafico-section" id="seccion-pie-descanso"></div>
      `;

      // ==================== TEMPERATURA ====================
      this.renderSeccionGrafico('seccion-temperatura', 'Temperatura de Motor', [
        {
          id: 'chartTemp',
          title: 'Temperatura a lo largo del tiempo',
          series: series.temperatura,
          color: '#e74c3c',
          bgColor: 'rgba(231, 76, 60, 0.1)',
          unit: '°C',
          type: 'line'
        }
      ], series.temperatura);

      // ==================== HORÓMETRO ====================
      this.renderSeccionGrafico('seccion-horometro', 'Hor\u00f3metro (Horas de motor)', [
        {
          id: 'chartHorometro',
          title: 'Acumulado de horas de motor',
          series: series.horometro,
          color: '#f39c12',
          bgColor: 'rgba(243, 156, 18, 0.1)',
          unit: ' hrs',
          type: 'line'
        }
      ], series.horometro, 'Sin registros de hor\u00f3metro en este rango. Los datos aparecen desde que se activ\u00f3 el c\u00e1lculo en el webhook.');

      // ==================== ODÓMETRO ====================
      this.renderSeccionGrafico('seccion-odometro', 'Od\u00f3metro (Distancia recorrida)', [
        {
          id: 'chartOdometro',
          title: 'Distancia acumulada',
          series: series.odometro,
          color: '#8e44ad',
          bgColor: 'rgba(142, 68, 173, 0.1)',
          unit: ' km',
          type: 'line'
        }
      ], series.odometro);

      // ==================== USO DE CLUTCH ====================
      this.renderSeccionGrafico('seccion-clutch', 'Uso de Clutch', [
        {
          id: 'chartClutch',
          title: 'Horas acumuladas de uso de clutch',
          series: series.uso_clutch,
          color: '#3498db',
          bgColor: 'rgba(52, 152, 219, 0.1)',
          unit: ' h',
          type: 'line'
        }
      ], series.uso_clutch);

      // ==================== PIE EN DESCANSO ====================
      this.renderSeccionGrafico('seccion-pie-descanso', 'Pie en Descanso', [
        {
          id: 'chartPieDescanso',
          title: 'Pie en descanso a lo largo del tiempo',
          series: series.pie_en_descanso,
          color: '#27ae60',
          bgColor: 'rgba(39, 174, 96, 0.1)',
          unit: ' s',
          type: 'line'
        }
      ], series.pie_en_descanso);

    } catch (error) {
      console.error('Error cargando sensores históricos:', error);
      if (body) body.innerHTML = '<div style="padding:2rem; text-align:center; color:var(--danger);">Error al cargar los datos</div>';
      if (status) status.textContent = 'Error';
    }
  },

  renderSeccionGrafico(containerId, titulo, graficos, seriesData, mensajeVacio = 'Sin datos disponibles para este sensor') {
    const container = document.getElementById(containerId);
    if (!container) return;

    if (!seriesData || seriesData.length === 0) {
      container.innerHTML = `
        <div class="grafico-group">
          <div class="grafico-group-title">${titulo}</div>
          <div style="padding:1rem; text-align:center; color:var(--text-muted); font-size:0.82rem;">${mensajeVacio}</div>
        </div>
      `;
      return;
    }

    // Calcular resumen
    const valores = seriesData.map(d => d.valor);
    const min = Math.min(...valores).toFixed(2);
    const max = Math.max(...valores).toFixed(2);
    const prom = (valores.reduce((a, b) => a + b, 0) / valores.length).toFixed(2);
    const ultimo = valores[valores.length - 1]?.toFixed(2) || 'N/A';
    const primero = valores[0]?.toFixed(2) || 'N/A';
    const diff = (valores[valores.length - 1] - valores[0]).toFixed(2);

    // Determinar si es acumulativo (horómetro, clutch)
    const esAcumulativo = ['chartHorometro', 'chartClutch', 'chartOdometro'].includes(graficos[0]?.id);

    const resumenHtml = esAcumulativo
      ? `<div class="grafico-resumen">
           <div class="grafico-resumen-item"><span class="grafico-resumen-label">Inicial:</span><span class="grafico-resumen-val">${primero}${graficos[0].unit}</span></div>
           <div class="grafico-resumen-item"><span class="grafico-resumen-label">Final:</span><span class="grafico-resumen-val">${ultimo}${graficos[0].unit}</span></div>
           <div class="grafico-resumen-item"><span class="grafico-resumen-label">Consumo/Uso:</span><span class="grafico-resumen-val" style="font-weight:700; color:var(--primary);">${diff}${graficos[0].unit}</span></div>
           <div class="grafico-resumen-item"><span class="grafico-resumen-label">Min:</span><span class="grafico-resumen-val">${min}</span></div>
           <div class="grafico-resumen-item"><span class="grafico-resumen-label">Max:</span><span class="grafico-resumen-val">${max}</span></div>
         </div>`
      : `<div class="grafico-resumen">
           <div class="grafico-resumen-item"><span class="grafico-resumen-label">Min:</span><span class="grafico-resumen-val">${min}${graficos[0].unit}</span></div>
           <div class="grafico-resumen-item"><span class="grafico-resumen-label">Max:</span><span class="grafico-resumen-val">${max}${graficos[0].unit}</span></div>
           <div class="grafico-resumen-item"><span class="grafico-resumen-label">Promedio:</span><span class="grafico-resumen-val">${prom}${graficos[0].unit}</span></div>
           <div class="grafico-resumen-item"><span class="grafico-resumen-label">\u00daltimo:</span><span class="grafico-resumen-val">${ultimo}${graficos[0].unit}</span></div>
         </div>`;

    let canvasHtml = '';
    graficos.forEach(g => {
      canvasHtml += `
        <div style="margin-bottom:0.5rem;">
          <div style="font-size:0.78rem; color:var(--gray-600); margin-bottom:0.25rem;">${g.title}</div>
          <div style="position:relative; height:220px;">
            <canvas id="${g.id}"></canvas>
          </div>
        </div>
      `;
    });

    const registroId = `registros-${containerId}`;
    const unit = graficos[0].unit;
    const esAcum = esAcumulativo;

    // Tabla de registros (oculta por default)
    const registrosHtml = `
      <div id="${registroId}" style="display:none; margin-top:0.5rem;">
        <div style="max-height:250px; overflow-y:auto; border:1px solid var(--gray-200); border-radius:6px;">
          <table style="width:100%; border-collapse:collapse; font-size:0.78rem;">
            <thead style="position:sticky; top:0; background:var(--gray-100);">
              <tr>
                <th style="padding:0.35rem 0.6rem; text-align:left; border-bottom:1px solid var(--gray-200);">Fecha</th>
                <th style="padding:0.35rem 0.6rem; text-align:right; border-bottom:1px solid var(--gray-200);">Valor</th>
                ${esAcum ? '<th style="padding:0.35rem 0.6rem; text-align:right; border-bottom:1px solid var(--gray-200);">Consumo</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${seriesData.map((d, i) => {
                const consumo = esAcum && i > 0 ? (d.valor - seriesData[i - 1].valor) : 0;
                const consumoStr = consumo > 0 ? `+${consumo.toFixed(2)}` : consumo === 0 && i > 0 ? '0' : '-';
                const consumoColor = consumo > 0 ? 'color:var(--primary); font-weight:600;' : '';
                return `
                <tr style="border-bottom:1px solid var(--gray-100);">
                  <td style="padding:0.3rem 0.6rem;">${new Date(d.fecha).toLocaleString('es-MX', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', second:'2-digit' })}</td>
                  <td style="padding:0.3rem 0.6rem; text-align:right; font-weight:500;">${d.valor}${unit}</td>
                  ${esAcum ? `<td style="padding:0.3rem 0.6rem; text-align:right; ${consumoColor}">${consumoStr}${consumo !== 0 || i > 0 ? unit : ''}</td>` : ''}
                </tr>`;
              }).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;

    container.innerHTML = `
      <div class="grafico-group">
        <div style="display:flex; align-items:center; justify-content:space-between;">
          <div class="grafico-group-title" style="border-bottom:none; margin-bottom:0; padding-bottom:0.25rem; flex:1;">${titulo}</div>
          <button class="btn btn-sm" style="font-size:0.72rem; padding:0.2rem 0.5rem; background:var(--gray-100); border:1px solid var(--gray-300); color:var(--gray-700);" onclick="trackingView.toggleRegistros('${registroId}', this)">Mostrar registros</button>
        </div>
        <div style="border-bottom:2px solid var(--gray-200); margin-bottom:0.5rem;"></div>
        ${resumenHtml}
        ${canvasHtml}
        ${registrosHtml}
      </div>
    `;

    // Renderizar cada gráfico
    graficos.forEach(g => {
      this.crearGrafico(g.id, g.series, g.color, g.bgColor, g.unit, g.type, esAcum);
    });
  },

  toggleRegistros(registroId, btn) {
    const el = document.getElementById(registroId);
    if (!el) return;
    const visible = el.style.display !== 'none';
    el.style.display = visible ? 'none' : 'block';
    btn.textContent = visible ? 'Mostrar registros' : 'Ocultar registros';
  },

  crearGrafico(canvasId, seriesData, color, bgColor, unit, type, esAcumulativo) {
    const canvas = document.getElementById(canvasId);
    if (!canvas || !seriesData || seriesData.length === 0) return;

    const labels = seriesData.map(d => new Date(d.fecha));
    const values = seriesData.map(d => d.valor);

    new Chart(canvas, {
      type: type || 'line',
      data: {
        labels,
        datasets: [{
          data: values,
          borderColor: color,
          backgroundColor: bgColor,
          borderWidth: 1.5,
          pointRadius: seriesData.length > 100 ? 0 : 2,
          pointHoverRadius: 4,
          fill: true,
          tension: 0.3
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => {
                if (!items[0]) return '';
                return new Date(items[0].parsed.x).toLocaleString('es-MX', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                });
              },
              label: (item) => {
                const val = `${item.parsed.y.toFixed(2)}${unit}`;
                if (!esAcumulativo || item.dataIndex === 0) return val;
                const prev = values[item.dataIndex - 1];
                const consumo = (item.parsed.y - prev).toFixed(2);
                return consumo > 0
                  ? [val, `Consumo: +${consumo}${unit}`]
                  : val;
              }
            }
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              tooltipFormat: 'dd/MM/yyyy HH:mm',
              displayFormats: {
                hour: 'dd/MM HH:mm',
                day: 'dd/MM',
                week: 'dd/MM',
                month: 'MMM yyyy'
              }
            },
            ticks: {
              font: { size: 10 },
              maxTicksLimit: 10
            },
            grid: { display: false }
          },
          y: {
            ticks: {
              font: { size: 10 },
              callback: (val) => `${val}${unit}`
            },
            grid: { color: 'rgba(0,0,0,0.05)' }
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        }
      }
    });
  },

  actualizarHoraUpdate() {
    const el = document.getElementById('lastUpdate');
    if (el) {
      const now = new Date();
      el.textContent = `Actualizado: ${now.toLocaleTimeString('es-MX')}`;
    }
  },

  hoyLocal() {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
};
